import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Box, Dialog } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { BoardCommand } from 'src/components/dispatch/assignment/types';
import {
  DispatchAssignmentNotifications,
  DispatchAssignmentTimelineSource,
  useDispatchAssignment,
} from 'src/components/dispatch/assignment/useDispatchAssignment';
import { DispatchProgressToast } from 'src/components/dispatch/DispatchProgressToast';
import { buildPlanningEntity } from 'src/components/dispatch/autoDispatch/buildPlanningEntity';
import { formatAutoDispatchFailure } from 'src/components/dispatch/autoDispatch/formatAutoDispatchFailure';
import { resolveAutoDispatchAvailability } from 'src/components/dispatch/autoDispatch/resolveAutoDispatchAvailability';
import { usePlanningDialogSettings } from 'src/components/dispatch/autoDispatch/usePlanningDialogSettings';
import { resolveAutoDispatchSelectionReason } from 'src/components/dispatch/autoDispatchSelection';
import { DraggedKind, dragEndAnalytics, dragStartAnalytics } from 'src/components/dispatch/dragAnalytics';
import { resolveProgressToastLabel } from 'src/components/dispatch/progressToastLabel';
import { buildNotAllocReasonsByOrderId } from 'src/components/dispatch/buildNotAllocReasonsByOrderId';
import { DailyVehicleMetric } from 'src/components/dispatch/dailyVehicleMetrics';
import {
  DetailPanelClickRect,
  DetailPanelShell,
} from 'src/components/dispatch/detailPanel/DetailPanelShell';
import { DetailPanelMoveTarget } from 'src/components/dispatch/detailPanel/types';
import PanelResizeHandle from 'src/components/dispatch/panelResize/PanelResizeHandle';
import {
  fitPanelWidths, isPanelWidth, reservedWidthFor,
} from 'src/components/dispatch/panelResize/panelWidth';
import { parseUnassignedIds } from 'src/components/dispatch/parseUnassignedIds';
import { resolvePrintTargetShiftIds } from 'src/components/dispatch/printTargets';
import { collectResetTargets } from 'src/components/dispatch/resetTargets';
import {
  allSelectedShiftsHaveNoPlan,
  collectSelectedDeliveryIdsForPlanning,
} from 'src/components/dispatch/selectedShiftTargets';
import TariffCalculationDialog from 'src/components/dispatch/TariffCalculationDialog';
import { buildTruckColorMap } from 'src/components/dispatch/truckColorMap';
import { displayMetricScopeOf, useDisplayMetrics } from 'src/components/dispatch/unassignedDisplay/useDisplayMetrics';
import { readLaneItemIds } from 'src/components/dispatch/unassignedDisplay/laneDragData';
import {
  useUnassignedDisplayMode,
} from 'src/components/dispatch/unassignedDisplay/useUnassignedDisplayMode';
import { useApplyResetToCaches } from 'src/components/dispatch/useApplyResetToCaches';
import { useOrderAttrFilter } from 'src/components/dispatch/useOrderAttrFilter';
import { useTariffCalculationAction } from 'src/components/dispatch/useTariffCalculationAction';
import { useVehicleAssignmentEditing } from 'src/components/dispatch/vehicleAssignment/useVehicleAssignmentEditing';
import { VehicleAssignmentMenus } from 'src/components/dispatch/vehicleAssignment/VehicleAssignmentMenus';
import {
  applyVehicleOverrides,
  useVehicleDisplayOverrides,
} from 'src/components/dispatch/vehicleAssignment/vehicleDisplayOverrides';
import { filterVehicleRowGroups, VehicleFilterMode } from 'src/components/dispatch/vehicleFilter';
import { LoadTotals } from 'src/components/dispatch/vehicleLoad';
import { hasDispatchableCard } from 'src/components/dispatch/vehicleRowExpand/assignedDelivery';
import ReportPrintingDialogs from 'src/components/ReportPrinting/ReportPrintingDialogs';
import { useReportPrinting } from 'src/components/ReportPrinting/useReportPrinting';
import { appBarHeight } from 'src/constants/layout';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { DispatchCard, UnassignedItem } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { PlanningSettingRequest } from 'src/entities/PlanningHistory.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY } from 'src/hooks/useConvenienceDispatchDeliveries.query';
import {
  useMutationDispatchCellColors, useQueryDispatchCellColors,
} from 'src/hooks/useDispatchCellColors';
import { useMutationTruckShiftRest } from 'src/hooks/useMutationTruckShiftRest';
import { useMutationPlanningV5 } from 'src/hooks/useQueryPlanning';
import { PlanningsDeliveriesQueryResult } from 'src/hooks/useQueryPlanningsDeliveries';
import { useQueryPlanningsNotAllocReasons } from 'src/hooks/useQueryPlanningsNotAllocReasons';
import { useMutationPlanningTruckDisplayOrders } from 'src/hooks/useQueryPlanningsTrucks';
import {
  SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY,
  ShipmentOrderLegRunsQueryResult,
} from 'src/hooks/useQueryShipmentOrderLegRuns';
import { useMutationShipmentOrder } from 'src/hooks/useQueryShipmentOrders';
import { useQueryTruckShifts } from 'src/hooks/useQueryTruckShifts';
import { useMutationShipmentOrderLegRun } from 'src/hooks/useShipmentOrderLegRun';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';
import {
  buildVehicleRowGroups,
  createVehicleRowGroupsCache,
  RotationRow,
  toUnassignedItem,
  VehicleRowGroupsCache,
} from 'src/pages/ConvenienceDispatches/adapter';
import { applyOverrideRowCards } from 'src/pages/ConvenienceDispatches/applyOverrideRowCards';
import { shouldResetOverrideAfterServerRefresh } from 'src/pages/ConvenienceDispatches/autoDispatchQueue';
import { VehicleTariffTotals } from 'src/pages/ConvenienceDispatches/buildGrossProfitByTruckId';
import ConvenienceDispatchAddVehicleDialog from 'src/pages/ConvenienceDispatches/ConvenienceDispatchAddVehicleDialog';
import ConvenienceDispatchUnassignedList from 'src/pages/ConvenienceDispatches/ConvenienceDispatchUnassignedList';
import ConvenienceDispatchVehicleList from 'src/pages/ConvenienceDispatches/ConvenienceDispatchVehicleList';
import DispatchDetailDialogs from 'src/pages/ConvenienceDispatches/DispatchDetailDialogs';
import { resolveCompletionEffects } from 'src/pages/ConvenienceDispatches/resolveCompletionEffects';
import {
  buildLegRunMaterialsMap,
  buildRowIdToShiftTruckId,
  buildRowLookup,
  resolveShipmentOrderIdsFromUnassignedIds,
} from 'src/pages/ConvenienceDispatches/rowLookup';
import {
  buildPlanningScopeKey,
  resolveSortedTruckIds,
} from 'src/pages/ConvenienceDispatches/truckOrder';
import { useDispatchDetailActions } from 'src/pages/ConvenienceDispatches/useDispatchDetailActions';
import { useUnassignedRemovalOptimistic } from 'src/pages/ConvenienceDispatches/useUnassignedRemovalOptimistic';
import { buildVehicleRows } from 'src/pages/ConvenienceDispatches/vehicleRowVM';
import { buildVehicleExtraMap } from 'src/pages/Map/buildVehicleExtraMap';
import { buildDisplayDays } from 'src/pages/Map/displayDays';
import { collectSelectedShiftTruckIds } from 'src/pages/Map/fixedToggle';
import { VehicleColorPickerPopover } from 'src/pages/Map/presenters/VehicleColorPickerPopover';
import { resolveVehicleColor } from 'src/pages/Map/vehicleColorPalette';
import { extractApiErrorMessages } from 'src/pages/TruckShifts/notifyBulkError';
import { mapShiftsToCells } from 'src/pages/TruckShifts/truckShiftGrid';
import { PLANNING_DIALOG_MAX_WIDTH } from 'src/pages/V3Plans/presenters/planningDialogSize';
import PlanningPresenter from 'src/pages/V3Plans/presenters/PlanningPresenter';
import { compareTrucksByDisplayOrder } from 'src/utils/truckDisplayOrder.util';
import { RelaxedRuleValues, RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import {
  autoDispatchCompletedNotification,
  autoDispatchStartedNotification,
} from './autoDispatchNotification';
import { buildBoardCommand, DraggedItem, DropLocation } from './buildBoardCommand';
import { buildLaneRowGroups } from './buildLaneRowGroups';
import { buildLongHaulLanes, orderLanesByTruckIds } from './buildLongHaulLanes';
import { buildLongHaulMoveTargets } from './buildLongHaulMoveTargets';
import { buildWorkTargets, RawShiftTime } from './buildWorkTargets';
import { LONG_HAUL_DND_CONTEXT_ID, readTripDragData, readWorkDropData } from './dragData';
import { LongHaulDragOverlayContent, resolveDragOverlayContent } from './dragOverlay';
import {
  allocatedDropped, DroppedAssignment, dropAssignNotification, trackDrop,
} from './dropAssignNotification';
import {
  longHaulDayColWidth, LONG_HAUL_ROW_HEIGHT, LONG_HAUL_VEHICLE_CARD_HEIGHT,
} from './layout';
import LongHaulBoard from './LongHaulBoard';
import { LongHaulDragOverlayCard } from './LongHaulDragOverlayCard';
import { LongHaulRestPopover } from './LongHaulRestPopover';
import {
  PENDING_ASSIGNMENT_TARIFF_REASON, resolveLongHaulTariffAvailability,
} from './longHaulTariffAvailability';
import LongHaulToolbar from './LongHaulToolbar';
import LongHaulTripDetail from './LongHaulTripDetail';
import {
  DEFAULT_UNASSIGNED_PANEL_WIDTH, UNASSIGNED_PANEL_WIDTH_STORAGE_KEY,
} from './panelWidth';
import {
  DEFAULT_CELL_COLORS, DispatchCellColorSettings, resolveCellColor,
} from './resolveCellColor';
import { resolveDirectStopCard, resolveTripLoadingCard } from './resolveDirectStopCard';
import { spanDaysOf } from './spanDays';
import { Lane, LongHaulTrip } from './types';

import type { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import type { TruckShiftRestRequest } from 'src/hooks/useMutationTruckShiftRest';

/**
 * 左カラムの幅。既存の車両リストを流用するので、同じカードを出すカード型配車と同じ 320 にする
 * （専用リストだった頃の 260 では運送会社名 / 車両名の 1 段目が潰れる）。
 */
const VEHICLE_COLUMN_WIDTH = 320;

/** 未割当リストが張るドロップ先の id（UnassignedDropArea.tsx） */
const UNASSIGNED_DROPPABLE_ID = 'unassigned';

const boardPointerSensorOptions = { activationConstraint: { distance: 3 } };

/**
 * 積載超過の判定に使う行ごとの積載量。この画面は「制約違反のある車両のみ」を出さないので
 * 空で渡す（filterVehicleRowGroups が読むのはその絞り込みのときだけ）。
 * 毎レンダー新しい Map を作ると絞り込みの memo が毎回外れるのでモジュール定数にする。
 */
const EMPTY_PEAK_LOAD: ReadonlyMap<string, LoadTotals> = new Map();

/**
 * 左カラムで隠す値。レーンは期間全体（最大 10 日）にまたがるので、「1 日の 1 回転」に
 * 紐づく積載量・粗利・勤務時間・回転番号を出すと何の数字なのか読めなくなる。
 *
 * 納品 / 積込 タグも同じ。左カラムへ渡すのは代表行（最初の運行）だけなので、レーンに
 * 3 運行あっても最初の 1 本の地点数しか数えない。サンプル（route-planner
 * `MatchingPresenter/index.tsx:1902`）も複数日表示では納品タグを出さない。
 */
const LONG_HAUL_HIDDEN_DAILY_METRICS: ReadonlySet<DailyVehicleMetric> = new Set([
  'capacity', 'grossProfit', 'driverShift', 'rotation', 'deliveryTag', 'pickupTag',
]);

/** 粗利は隠すので算出しない。参照を固定するためモジュール定数にする */
const EMPTY_GROSS_PROFIT: ReadonlyMap<number, VehicleTariffTotals> = new Map();

/** 車両グループでの絞り込みは出さない。空なら絞り込みメニューにセクションごと出ない */
const NO_TRUCK_GROUPS: PlanningsGroupEntity[] = [];
const NO_TRUCK_GROUP_INDICES: number[] = [];

/**
 * 車両リストが必須で受け取るハンドラのうち、この画面に対応する操作が無いもの。
 * 回転の追加・削除は `LONG_HAUL_HIDDEN_DAILY_METRICS` でボタンごと出さないので押せない。
 */
const noop = () => undefined;

/** `lr-123` → 123。未割当アイテムの id から legRun を引く */
const legRunIdOfItemId = (itemId: string): number | null => {
  const matched = /^lr-(\d+)$/.exec(itemId);
  return matched ? Number(matched[1]) : null;
};

/**
 * API が返した本文（`errors` / `message`）をそのまま出す。
 * 「配車済みの案件があります」＋どの案件か、のような明細が入っているので潰さない。
 */
const apiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { message?: unknown }; status?: number } } | null)?.response;
  const messages = extractApiErrorMessages(response?.data);
  if (messages.length > 0) return messages.join('\n');
  // 一括削除・配車リセットは `{ message: '...' }` で返す（既存 2 画面と同じ読み方）
  if (typeof response?.data?.message === 'string') return response.data.message;
  return response?.status ? `${fallback}（エラー ${response.status}）` : fallback;
};

type Props = {
  startOn: string;
  endOn: string;
  deliveriesQuery: PlanningsDeliveriesQueryResult;
  legRunsQuery: ShipmentOrderLegRunsQueryResult;
  trucks: PlanningsTruckEntity[];
  drivers: PlanningsDriverEntity[];
  /** 未割当リストの 32 列フィルタへ素通しする値。ConvenienceDispatches/Presenter.tsx と同じ契約 */
  customInputFieldNames: string[];
  trucksForFilter: { id: number; name: string }[];
  driversForFilter: { id: number; name: string }[];
  companyId: string;
  /** 0 は全社表示・null はライセンス未取得。休み設定のクエリはこれが決まってから投げる */
  selectedCompanyId: number | null;
  isLicenseReady: boolean;
  isMastersReady: boolean;
  isMastersError: boolean;
  /** license.config.delivery_list_grouping（表示設定 > 配送リストの地点表示） */
  deliveryListGrouping?: DeliveryListGroupingValues;
  /** license.config.use_algorithm_planning。自動配車ボタンのライセンス層（/map と同じ判定） */
  useAlgorithmPlanning: boolean;
  /** ツールバーの画面遷移。Container の useNavigate を包んで渡す（/map と同じ形） */
  onNavigate: (path: string) => void;
  /**
   * 日付範囲が確定したときに呼ぶ。`/long-haul` は日付を URL に持つので、呼び出し側は
   * `navigate('/long-haul/{startOn}/{endOn}')` する
   */
  onDateRangeChange: (startOn: string, endOn: string) => void;
};

/**
 * 長距離配車の 3 カラム。左 = 車両、中央 = 日付軸のボード、右 = 未割当。
 *
 * データは `/map` と同じ順路で組む（legRun の補完 → 行 → 勤務単位のグループ）。
 * そのうえで **必ず楽観的上書きを通す**（`applyOverrideRowCards` / `overrideUnassigned`）。
 * 通さないと D&D してもサーバ応答まで画面が動かない。
 */
const LongHaulPresenter: FC<Props> = ({
  startOn,
  endOn,
  deliveriesQuery,
  legRunsQuery,
  trucks,
  drivers,
  customInputFieldNames,
  trucksForFilter,
  driversForFilter,
  companyId,
  selectedCompanyId,
  isLicenseReady,
  isMastersReady,
  isMastersError,
  deliveryListGrouping,
  useAlgorithmPlanning,
  onNavigate,
  onDateRangeChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const deliveries = useMemo(
    () => deliveriesQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [deliveriesQuery.data],
  );
  const legRuns = useMemo(
    () => legRunsQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [legRunsQuery.data],
  );

  const days = useMemo(() => buildDisplayDays(startOn, endOn), [startOn, endOn]);
  /** 日列の幅。3 日以下は広く取る（`longHaulDayColWidth`） */
  const dayColWidth = longHaulDayColWidth(days.length);

  // ── 右カラムの開閉と幅（既存 2 画面と同じ仕組み。#338 C3） ─────────
  const [isUnassignedOpen, setIsUnassignedOpen] = useState(true);
  const [unassignedWidth, setUnassignedWidth] = usePersistedJSON<number>(
    UNASSIGNED_PANEL_WIDTH_STORAGE_KEY,
    DEFAULT_UNASSIGNED_PANEL_WIDTH,
    isPanelWidth,
  );
  // 幅の transition は掴んでいる間だけ切る。効いたままだと指の動きに 0.2s 遅れて追いかけてくる
  const [unassignedResizing, setUnassignedResizing] = useState(false);
  const toggleUnassignedPanel = useCallback(() => setIsUnassignedOpen((prev) => !prev), []);

  // 3 ペインを並べている器の実測幅。保存幅がこれを超えるときは描画時に落とす（fitPanelWidths）
  const paneContainerRef = useRef<HTMLDivElement>(null);
  const [paneContainerWidth, setPaneContainerWidth] = useState(0);
  // Why useLayoutEffect: 初回描画で幅が 0 のままだと、器に収まらない保存幅がそのまま
  // 1 フレーム描かれてから縮む（はみ出しが一瞬見える）
  useLayoutEffect(() => {
    const element = paneContainerRef.current;
    if (!element) return undefined;
    const measure = () => setPaneContainerWidth(element.getBoundingClientRect().width);
    measure();
    // ResizeObserver ならサイドナビの開閉のように window の resize を伴わない変化も拾える。
    // jsdom には無いので、その環境では window の resize で代替する
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  /**
   * 器に収まる幅へ落とす。共有の `fitPanelWidths` は左右とも可変な画面（/map・カード配車）の
   * ために作られているので、固定の左カラム幅を `left` として渡し、返る右だけを使う。
   *
   * Why not 専用の計算を書く: 跳ね防止（左を優先して残す）はレビューで 3 度直した計算で、
   * 二重に持つと片方だけ直す事故になる（panelWidth.ts のコメント）。
   *
   * Why ハンドル 1 本ぶんのずれを許す: `fitPanelWidths` / `reservedWidthFor` はハンドルを
   * 左右 2 本と数えるが、この画面は右に 1 本しか置かない（左カラムは可変にしない）。
   * 差は 6px で、中央へ余分に残す向きにしか効かない。共有側に分岐を足すと既存 2 画面の
   * 挙動を変える危険があるので、こちらが 6px を譲る。
   */
  const { right: fittedUnassignedWidth } = useMemo(
    () => fitPanelWidths({
      containerWidth: paneContainerWidth,
      left: VEHICLE_COLUMN_WIDTH,
      right: unassignedWidth,
      rightOpen: isUnassignedOpen,
    }),
    [paneContainerWidth, unassignedWidth, isUnassignedOpen],
  );

  // ── サーバ由来の盤面（/map・カード配車と同じ順路） ───────────────
  const unassignedForVM = useMemo(() => legRuns.filter((lr) => !lr.assigned), [legRuns]);

  // `/plannings/.../deliveries` の shipmentOrderLegRuns[] は compact で数量・指定時間などが
  // 抜けている。非 compact の `/shipment_order_leg_runs` と id で突き合わせて補う
  const legRunMaterialsMap = useMemo(() => buildLegRunMaterialsMap(legRuns), [legRuns]);
  const enrichedDeliveries = useMemo(() => {
    if (legRunMaterialsMap.size === 0) return deliveries;
    return deliveries.map((d) => ({
      ...d,
      shipmentOrderLegRuns: d.shipmentOrderLegRuns.map((lr) => {
        if (lr.id == null) return lr;
        const rich = legRunMaterialsMap.get(lr.id);
        return rich ? { ...lr, ...rich } : lr;
      }),
    }));
  }, [deliveries, legRunMaterialsMap]);

  const sourceRows = useMemo(
    () => buildVehicleRows(enrichedDeliveries, null, unassignedForVM, trucks, drivers, deliveryListGrouping),
    [enrichedDeliveries, unassignedForVM, trucks, drivers, deliveryListGrouping],
  );

  const extraMap = useMemo(
    () => buildVehicleExtraMap(deliveries, trucks, drivers),
    [deliveries, trucks, drivers],
  );
  // 1 台に複数の勤務が乗るので、勤務ごとのドライバー名は truckId 代表の extraMap ではなくここから引く
  const driverNameById = useMemo(() => new Map(drivers.map((d) => [d.id, d.name])), [drivers]);

  // 変わっていないグループは同じオブジェクトを返す（行の memo を効かせる）
  const rowGroupsCacheRef = useRef<VehicleRowGroupsCache | null>(null);
  rowGroupsCacheRef.current ??= createVehicleRowGroupsCache();
  const rowGroupsCache = rowGroupsCacheRef.current;
  const serverRowGroups = useMemo(
    () => buildVehicleRowGroups(sourceRows, extraMap, driverNameById, rowGroupsCache),
    [sourceRows, extraMap, driverNameById, rowGroupsCache],
  );

  const serverUnassigned = useMemo<UnassignedItem[]>(
    () => unassignedForVM.map((lr, idx) => toUnassignedItem(lr, idx)),
    [unassignedForVM],
  );

  // ── 楽観的上書き + 自動配車 ──────────────────────────────────
  // 適用に要る rowLookup は上書きから作るので（上書き → 行 → 上書き の循環）、
  // 描画後に同期した ref から読む。適用は操作のハンドラからしか起きない
  const rowLookupRef = useRef<ReadonlyMap<string, RotationRow>>(new Map());
  const getRowLookupForApply = useCallback(() => rowLookupRef.current, []);
  const autoDispatchSourceRef = useRef<DispatchAssignmentTimelineSource>({
    deliveries: [], rowGroups: [], rowIdToShiftTruckId: new Map(), truckIdToDeliveryId: new Map(),
  });
  const getTimelineSource = useCallback(() => autoDispatchSourceRef.current, []);

  const [selectedUnassignedIds, setSelectedUnassignedIds] = useState<Set<string>>(new Set());
  // 動かした案件の選択は落とす（動かした先でも選ばれたままだと、次の操作が巻き込む）
  const handleAfterApply = useCallback((_command: BoardCommand) => {
    setSelectedUnassignedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  /**
   * 未割当理由バッジ（log_web_issues#338）。カード配車（ConvenienceDispatches/Presenter.tsx:981-990）
   * の 4 ステップ（session を持つ → 完了で拾う → 理由を引く → legRunId で束ねて渡す）を写す。
   *
   * API の `order_id` は常に leg_run.id なので、そのまま key にする
   * （buildNotAllocReasonsByOrderId の Why not コメント参照。両建てにすると無関係な案件へ理由が付く）。
   */
  const [notAllocSessionId, setNotAllocSessionId] = useState<string | null>(null);

  /**
   * D&D で割り付けた結果の通知。落とした時点では出さず、自動配車が完了してから出す。
   *
   * 落とした瞬間に「{地点名} を割り付けました」を出していたが、実際に割り付くのは
   * サーバの自動配車が終わってから（applyCommand は 2000ms 待ってから撃つ）なので、
   * まだ何も起きていない時点で成功を言い切っていた（ユーザー指摘 2026-09-29）。
   *
   * Why not 完了時に地点名を引き直す: applyCommand は落とした案件を未割当リストから
   * すぐ消すので、完了してから引くと文言の地点名だけが空になる。落とした時点で貯めて
   * ここに置き、文言にするのを完了まで遅らせる。
   */
  const pendingDropRef = useRef<DroppedAssignment | null>(null);

  // 通知。出す / 出さないと文言は autoDispatchNotification.ts（純粋関数・単体テスト済み）
  const assignmentNotifications = useMemo<DispatchAssignmentNotifications>(() => ({
    onStarted: ({ mode }) => {
      // 前回の理由を捨てる（既存 2 画面と同じ。残すと新しい実行の途中で古いバッジが出たままになる）
      setNotAllocSessionId(null);
      const notification = autoDispatchStartedNotification(mode);
      if (notification) enqueueSnackbar(notification.message, { variant: notification.variant });
    },
    onCompleted: (result, context) => {
      // 触る / 触らないの表はカード配車と共有する（timeline の即時完了は割当不可 ID を
      // 返さないので session に触らない、など）
      if (resolveCompletionEffects(context.mode, context.completionKind).updateNotAllocSession) {
        // 無条件に保持すると、未割当が 0 件の完了でも直前の session が残る
        const hasNotAlloc = result.notAllocatedOrderIds.length > 0 && result.sessionId != null;
        setNotAllocSessionId(hasNotAlloc ? result.sessionId : null);
      }
      /*
        D&D で割り付けたぶんは、自動配車の完了文言より具体的なのでそちらを先に出す。
        取り出したら捨てる（次にバーを動かしただけの完了で再び出ないように）。

        置けなかった案件は成功に数えない。共有フックがその legRun を未割当へ戻すので、
        数えるとカードが未割当リストへ戻っているのに「割り付けました」と言い切る
        （Codex レビュー指摘 2026-09-29）。
      */
      const dropped = dropAssignNotification(
        allocatedDropped(pendingDropRef.current, result.notAllocatedOrderIds),
      );
      pendingDropRef.current = null;
      if (dropped) enqueueSnackbar(dropped.message, { variant: dropped.variant });

      /*
        割り当たらなかったぶんは、D&D の成功文言があっても必ず伝える。握り潰すと
        「何件が入らなかったか」がどこにも出ない。全部入ったときだけ黙る
        （「自動配車が完了しました」を成功文言に重ねても足す情報が無い）。
      */
      if (dropped && result.notAllocatedOrderIds.length === 0) return;
      const notification = autoDispatchCompletedNotification(
        context.mode, result.notAllocatedOrderIds.length,
      );
      if (notification) enqueueSnackbar(notification.message, { variant: notification.variant });
    },
    /**
     * 期間・会社が変わったら捨てる。
     *
     * Why not カード配車に合わせて捨てない: あちらは単日を開く画面なので、日付を変えれば
     * 未割当の legRun も総入れ替わりになり、古い理由はどのカードにも一致しない。
     * 長距離配車は 7 日を開くので、1 日ずらすだけで前後の期間が 6 日ぶん重なる。
     * 捨てないと、別の期間に対して出した理由が同じ legRunId のカードへそのまま残る。
     * `/map` は同じ理由（会社切り替え）で既に捨てている。
     */
    onScopeChanged: () => {
      // 落としてから自動配車が撃たれるまでの待ち時間に期間を変えると、共有フックは
      // 楽観反映を捨てる。通知だけ残すと、変更先の完了で変更前の地点を知らせてしまう
      pendingDropRef.current = null;
      setNotAllocSessionId(null);
    },
    // 停止したときは 1 件も割り付いていないので理由も無い（既存 2 画面と同じ）。
    // 割り付けた通知も捨てる（割り付いていないのに「割り付けました」が出る）
    onCancelled: () => {
      pendingDropRef.current = null;
      setNotAllocSessionId(null);
    },
    onFailed: (error, context) => {
      pendingDropRef.current = null;
      // timeline の前段 reset で落ちると onStarted が呼ばれないため、ここで消さないと
      // 前回のバッジが残り続ける（`/map` の onFailed と同じ）
      setNotAllocSessionId(null);
      // 文言は共有ヘルパに任せる（既存 2 画面と同じ）。固定文言で潰すと、サーバが
      // 400 の本文に載せる「どの案件がどこと衝突したか」の明細がユーザーに届かない
      enqueueSnackbar(formatAutoDispatchFailure(error, context), { variant: 'error' });
    },
  }), [enqueueSnackbar]);

  const {
    applyCommand,
    controller,
    overrideRowCards,
    setOverrideRowCards,
    overrideUnassigned,
    setOverrideUnassigned,
    hasQueuedOverrideChangeRef,
  } = useDispatchAssignment({
    scope: { startOn, endOn, companyId },
    getRowLookup: getRowLookupForApply,
    getTimelineSource,
    legRuns,
    serverUnassigned,
    deliveryListGrouping,
    onAfterApply: handleAfterApply,
    notifications: assignmentNotifications,
  });

  /**
   * この画面の操作計測。`screen_name` を足すだけの薄い包み。
   *
   * 粒度は V3Plans に合わせる（`button_name` は日本語の操作名、`label` は対象の件数や種類）。
   */
  const sendAnalytics = useCallback((
    action: string, buttonName: string, label?: string | number,
  ) => {
    ReactGA.event(action, {
      screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
      button_name: buttonName,
      ...(label === undefined ? {} : { label }),
    });
  }, []);

  /**
   * 盤面を動かすコマンドの唯一の出口。
   *
   * D&D とパネルのボタンは同じ操作なので、通知の貯め（`pendingDropRef`）にも同じように
   * 効かせる。パネルの「未割り当てに戻す」だけ貯めを通らないと、落としたばかりの案件を
   * ボタンで戻したときに成功件数へ残る（Codex レビュー指摘 2026-09-29）。
   *
   * 地点名は D&D のときだけ引ける（掴んだ未割当カードから引く）。ボタン経由は
   * 盤面のバーが相手なので空で渡す。`trackDrop` が名前を使うのは足すときだけで、
   * ボタン経由で足す操作（レーン間移動）は貯めを変えない。
   */
  const runBoardCommand = useCallback((
    command: BoardCommand | null,
    nameByLegRunId: ReadonlyMap<number, string> = new Map(),
  ) => {
    if (!command) return;
    // 計測はここに置く。D&D もパネルのボタンも同じ出口を通るので、入口ごとに書かずに済む
    const analytics = dragEndAnalytics(command);
    if (analytics) sendAnalytics(analytics.action, analytics.buttonName, analytics.label);
    pendingDropRef.current = trackDrop(pendingDropRef.current, command, nameByLegRunId);
    applyCommand(command);
  }, [applyCommand, sendAnalytics]);


  // 未割当理由の本体。session が無い間は hook 側の `enabled: Boolean(sessionId)` で叩かない
  const { data: notAllocReasonsData } = useQueryPlanningsNotAllocReasons(notAllocSessionId ?? '');
  const notAllocReasonsByOrderId = useMemo(
    () => buildNotAllocReasonsByOrderId(notAllocReasonsData),
    [notAllocReasonsData],
  );

  const overrideStateRef = useRef({ overrideRowCount: 0, hasOverrideUnassigned: false });
  useLayoutEffect(() => {
    overrideStateRef.current = {
      overrideRowCount: overrideRowCards.size,
      hasOverrideUnassigned: overrideUnassigned !== null,
    };
  }, [overrideRowCards, overrideUnassigned]);

  // サーバデータが変わったときだけ上書きを捨てる。操作の途中（上書きが残っている間）は
  // 捨てない（捨てると D&D で置いた案件が消える）
  useLayoutEffect(() => {
    const overrideState = overrideStateRef.current;
    if (!shouldResetOverrideAfterServerRefresh({
      overrideRowCount: overrideState.overrideRowCount,
      hasOverrideUnassigned: overrideState.hasOverrideUnassigned,
      hasQueuedOverrideChange: hasQueuedOverrideChangeRef.current,
    })) return;
    // 既に空なら参照も変えない（上書き監視 effect が「編集された」と誤読して実行中の
    // 自動配車を自己キャンセルする）
    setOverrideRowCards((prev) => (prev.size === 0 ? prev : new Map()));
    setOverrideUnassigned((prev) => (prev === null ? prev : null));
  }, [sourceRows, serverUnassigned, setOverrideRowCards, setOverrideUnassigned, hasQueuedOverrideChangeRef]);

  // 実表示。ここを飛ばすと D&D してもサーバ応答まで画面が変わらない
  const rowGroups = useMemo(
    () => applyOverrideRowCards(serverRowGroups, overrideRowCards),
    [serverRowGroups, overrideRowCards],
  );
  const unassigned = overrideUnassigned ?? serverUnassigned;

  const rowLookup = useMemo(() => buildRowLookup(rowGroups), [rowGroups]);
  useLayoutEffect(() => { rowLookupRef.current = rowLookup; }, [rowLookup]);

  const rowIdToShiftTruckId = useMemo(() => buildRowIdToShiftTruckId(deliveries), [deliveries]);
  const truckIdToDeliveryId = useMemo(() => {
    const map = new Map<number, number>();
    deliveries.forEach((d) => { if (!map.has(d.truckId)) map.set(d.truckId, d.id); });
    return map;
  }, [deliveries]);
  useLayoutEffect(() => {
    autoDispatchSourceRef.current = {
      deliveries, rowGroups, rowIdToShiftTruckId, truckIdToDeliveryId,
    };
  }, [deliveries, rowGroups, rowIdToShiftTruckId, truckIdToDeliveryId]);

  // ── レーン ───────────────────────────────────────────────
  const [filterMode, setFilterMode] = useState<VehicleFilterMode>('すべて表示');
  const [selectedLaneIds, setSelectedLaneIds] = useState<Set<string>>(new Set());

  /**
   * 「選択中のみ表示する」は行 id で絞る（`filterVehicleRowGroups` の契約）ので、
   * 選択したレーンを行へ展開する。
   *
   * Why not レーンから rowId を読む: レーンは絞り込み後の行から作るので、ここで読むと
   * 選択 → 絞り込み → レーン → 選択 の循環になる。`laneId` は
   * `buildLongHaulLanes` が `truckId:driverId:分割番号` で作るので、分割番号を落とした
   * 前半で行を引く（分割レーンは同じ担当なので、片方を選ぶともう片方も残る）。
   */
  const selectedLaneKeys = useMemo(
    () => new Set(Array.from(selectedLaneIds).map((id) => id.slice(0, id.lastIndexOf(':')))),
    [selectedLaneIds],
  );
  const selectedRowIds = useMemo(() => {
    const ids = new Set<string>();
    if (selectedLaneKeys.size === 0) return ids;
    rowGroups.forEach((group) => group.rows.forEach((row) => {
      if (selectedLaneKeys.has(`${row.truckId}:${row.driverId ?? 'none'}`)) ids.add(row.rowId);
    }));
    return ids;
  }, [rowGroups, selectedLaneKeys]);

  const displayRowGroups = useMemo(
    () => filterVehicleRowGroups(rowGroups, {
      mode: filterMode,
      selectedRowIds,
      peakLoadByRowId: EMPTY_PEAK_LOAD,
    }),
    [rowGroups, filterMode, selectedRowIds],
  );

  // 勤務の実日時は raw delivery から引く（VehicleInfoExtra.shiftStart は HH:mm で日付が無い）
  const shiftTimeByDeliveryId = useMemo(() => {
    const map = new Map<number, RawShiftTime>();
    deliveries.forEach((d) => {
      map.set(d.id, { startAt: d.startAt, endAt: d.endAt, shiftId: d.shiftId ?? null });
    });
    return map;
  }, [deliveries]);

  const workTargets = useMemo(
    () => buildWorkTargets(displayRowGroups, shiftTimeByDeliveryId),
    [displayRowGroups, shiftTimeByDeliveryId],
  );
  /**
   * バーの文言を引くための legRun 索引。
   *
   * Why not legRunMaterialsMap だけ: あちらは `/shipment_order_leg_runs` 由来で、
   * 積地の指定開始時刻が期間内のものしか返らない（ShipmentOrderLegRuns::Search）。
   * 期間より前に積む割当済みの運行はここに無く、荷主・積地・降地がすべて空のバーになる
   * （dev 実測。2025-03-21 週で 10 本中 1 本）。delivery が持つ legRun を土台にし、
   * 非 compact の値が取れるものはそれで上書きする。
   */
  const legRunById = useMemo(() => {
    const map = new Map(legRunMaterialsMap);
    enrichedDeliveries.forEach((delivery) => {
      delivery.shipmentOrderLegRuns.forEach((legRun) => {
        if (legRun.id == null || map.has(legRun.id)) return;
        map.set(legRun.id, legRun);
      });
    });
    return map;
  }, [legRunMaterialsMap, enrichedDeliveries]);

  const serverLanes = useMemo(
    () => buildLongHaulLanes({
      groups: displayRowGroups,
      workTargets,
      legRunById,
    }),
    [displayRowGroups, workTargets, legRunById],
  );

  // ── 車両表示順（左カラムの並べ替えハンドル） ────────────────────
  /**
   * 並べ替えの保存先。`/map` とカード型配車を写経しつつ、掴める条件だけ変えている
   * （あちらは `startOn === endOn`）。
   *
   * Why 複数日でも掴める: 保存は開始日を `effective_on` とする版を作る操作で、読み出しは
   * 「`effective_on <= 対象日` の最新版」（planning_api の `TruckDisplayOrders::OrderResolver`）。
   * 開始日に書けば表示期間の全日に効くので、既定が 7 日のこの画面で掴めないままにする
   * 理由が無い。既存 2 画面の条件は変えていない。
   */
  const planningScopeKey = useMemo(
    () => buildPlanningScopeKey(companyId, startOn, endOn),
    [companyId, startOn, endOn],
  );
  const truckDisplayOrderMutation = useMutationPlanningTruckDisplayOrders(startOn, endOn);
  // useMutation の戻り値は毎レンダー新しいオブジェクト。mutate は安定なのでそちらを依存にする
  const truckDisplayOrderMutate = truckDisplayOrderMutation.mutate;
  const [sortedTruckIdsState, setSortedTruckIdsState] = useState<{ rangeKey: string; ids: number[] }>({
    rangeKey: '',
    ids: [],
  });
  const sortedTruckIds = useMemo(
    () => resolveSortedTruckIds(sortedTruckIdsState, planningScopeKey),
    [planningScopeKey, sortedTruckIdsState],
  );

  const handleTruckOrderChange = useCallback((visibleNextIds: number[]) => {
    // API は受け取った truck_ids だけで版を作り直すので、絞り込みで隠れている車両も含めた
    // 全車両を base にマージして送る（`/map` の handleTruckOrderChange と同じ）
    const baseTruckIds = sortedTruckIds.length > 0
      ? sortedTruckIds
      : trucks.slice().sort(compareTrucksByDisplayOrder).map((t) => t.id);

    const visibleSet = new Set(visibleNextIds);
    let pointer = 0;
    const mergedIds = baseTruckIds.map((id) => {
      if (visibleSet.has(id) && pointer < visibleNextIds.length) {
        const next = visibleNextIds[pointer];
        pointer += 1;
        return next;
      }
      return id;
    });
    while (pointer < visibleNextIds.length) {
      mergedIds.push(visibleNextIds[pointer]);
      pointer += 1;
    }

    // 楽観的更新。保存を待つと掴んだ行が一度元に戻る
    setSortedTruckIdsState({ rangeKey: planningScopeKey, ids: mergedIds });
    truckDisplayOrderMutate(mergedIds, {
      onError: () => {
        setSortedTruckIdsState({ rangeKey: '', ids: [] });
        enqueueSnackbar('車両表示順の保存に失敗しました', { variant: 'error' });
      },
    });
  }, [enqueueSnackbar, planningScopeKey, sortedTruckIds, trucks, truckDisplayOrderMutate]);

  /**
   * 表示するレーン。並べ替えは左カラムだけでなくレーンごと行う。
   * 中央のボードと左の行は 1 対 1 で並ぶので、片方だけ並べ替えると縦位置が合わなくなる。
   */
  const lanes = useMemo(
    () => orderLanesByTruckIds(serverLanes, sortedTruckIds),
    [serverLanes, sortedTruckIds],
  );

  const handleSelectLane = useCallback((laneId: string) => {
    setSelectedLaneIds((prev) => {
      const next = new Set(prev);
      if (next.has(laneId)) next.delete(laneId);
      else next.add(laneId);
      return next;
    });
  }, []);

  // ── 左カラム（既存の車両リストを流用） ──────────────────────
  // 1 レーン = 1 グループ = 1 行。行と laneId の対応づけは変換と同じ並びから作る
  const laneRowGroups = useMemo(() => buildLaneRowGroups(lanes), [lanes]);
  const laneIdByRowId = useMemo(
    () => new Map(laneRowGroups.map((group, index) => [group.rows[0].rowId, lanes[index].laneId])),
    [laneRowGroups, lanes],
  );

  /**
   * リストの行の選択状態。レーンの選択から引く。
   *
   * Why not `selectedRowIds` を使い回す: あちらは「選択中のみ表示する」の絞り込み用で、
   * 選んだレーンに載る**全ての**行（= 1 レーンに何本もある運行）を含む。リストに出るのは
   * レーンを代表する 1 行だけなので、そのまま渡すとヘッダの選択件数が運行の本数になる。
   */
  const listSelectedRowIds = useMemo(
    () => new Set(
      laneRowGroups
        .map((group) => group.rows[0].rowId)
        .filter((rowId) => selectedLaneIds.has(laneIdByRowId.get(rowId) ?? '')),
    ),
    [laneRowGroups, laneIdByRowId, selectedLaneIds],
  );
  const allLanesSelected = lanes.length > 0 && lanes.every((lane) => selectedLaneIds.has(lane.laneId));

  const handleRowToggle = useCallback((rowId: string) => {
    const laneId = laneIdByRowId.get(rowId);
    if (laneId != null) handleSelectLane(laneId);
  }, [laneIdByRowId, handleSelectLane]);
  const handleSelectAllLanes = useCallback(
    () => { setSelectedLaneIds(new Set(lanes.map((lane) => lane.laneId))); },
    [lanes],
  );
  const handleDeselectAllLanes = useCallback(() => { setSelectedLaneIds(new Set()); }, []);

  // ── 色バー（行の左端の縦帯）と色ピッカー ──────────────────────
  /**
   * 表示色の元は deliveries（`sourceRows`）。`/map` と同じ単一ソースにする
   * （trucks マスタ側を読むと、2 つのクエリが別々に取り直された瞬間だけ色が割れる）。
   */
  const truckColorByTruckId = useMemo(() => buildTruckColorMap(sourceRows), [sourceRows]);
  /**
   * 色が未設定のときのパレット位置。表示順（レーンの並び）から引くので、
   * 並べ替えると色も付け替わる（`/map` の orderIndexByTruckId と同じ規則）。
   * 1 台に複数レーンがあるときは最初のレーンの位置に揃える。
   */
  const orderIndexByTruckId = useMemo(() => {
    const map = new Map<number, number>();
    lanes.forEach((lane, index) => {
      if (!map.has(lane.truckId)) map.set(lane.truckId, index);
    });
    return map;
  }, [lanes]);
  // 車両リストは memo なので、参照を固定しないと選択のたびに全行が描き直される
  const vehicleColorFor = useCallback(
    (truckId: number) => resolveVehicleColor(
      truckColorByTruckId.get(truckId) ?? null,
      orderIndexByTruckId.get(truckId) ?? 0,
    ),
    [truckColorByTruckId, orderIndexByTruckId],
  );
  const [colorAnchor, setColorAnchor] = useState<{ el: HTMLElement; truckId: number } | null>(null);
  const handleVehicleColorClick = useCallback(
    (truckId: number, el: HTMLElement) => { setColorAnchor({ el, truckId }); },
    [],
  );
  const truckById = useMemo(() => new Map(trucks.map((t) => [t.id, t])), [trucks]);
  // ピッカーの「対象」見出しに出す車両名。左カラムの表示（truckName || vehicleNumber）に揃える
  const truckNameByTruckId = useMemo(() => {
    const map = new Map<number, string>();
    laneRowGroups.forEach(({ vehicle }) => {
      const { truckId, truckName, vehicleNumber } = vehicle;
      if (truckId != null && !map.has(truckId)) map.set(truckId, truckName || vehicleNumber);
    });
    return map;
  }, [laneRowGroups]);

  // ── 車両追加 / 帳票出力（左カラムのヘッダ） ──────────────────
  // どちらもカード配車・`/map` と同一実体。渡さないとボタンごと出ない
  // （`ConvenienceDispatchVehicleList` は handler の有無で描くかを決める）
  const [addVehicleDialogOpen, setAddVehicleDialogOpen] = useState(false);
  const openAddVehicleDialog = useCallback(() => setAddVehicleDialogOpen(true), []);
  const closeAddVehicleDialog = useCallback(() => setAddVehicleDialogOpen(false), []);

  // 既に勤務がある車両・ドライバーは候補から外す（二重追加を防ぐ）。
  // 合成行は実在の車両ではないので除く（`/map` と同じ絞り方）
  const existingTruckIds = useMemo(
    () => new Set(sourceRows.filter((row) => !row.isPlaceholderRow).map((row) => row.truckId)),
    [sourceRows],
  );
  const existingDriverIds = useMemo(() => new Set(deliveries.map((d) => d.driverId)), [deliveries]);

  /**
   * 帳票の対象勤務。レーンを選んでいればその勤務だけ、選んでいなければ期間中の全勤務
   * （`resolvePrintTargetShiftIds` の契約）。
   *
   * Why not `listSelectedRowIds`: あちらはレーンを代表する 1 行だけで、同じ担当が
   * 期間中に持つ他の日の勤務が落ちる。帳票はレーン全体を出すので、レーンを行へ
   * 展開した `selectedRowIds` から集める。
   */
  const printTargetShiftIds = useMemo(
    () => resolvePrintTargetShiftIds(collectSelectedShiftTruckIds(selectedRowIds, rowGroups), deliveries),
    [selectedRowIds, rowGroups, deliveries],
  );
  const reportPrinting = useReportPrinting({
    startOn,
    endOn,
    shiftIds: printTargetShiftIds,
    onAnalyticsEvent: (action, buttonName) => ReactGA.event(action, {
      screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
      button_name: buttonName,
    }),
  });

  // ── D&D ────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, boardPointerSensorOptions));
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const collisions = pointerWithin(args);
    return collisions.length > 0 ? collisions : rectIntersection(args);
  }, []);

  /** 未割当アイテムの id から、動かす legRun を決める（選択中なら選択ごと動かす） */
  const resolveDraggedUnassigned = useCallback((
    activeId: string,
    /** 地点単位のカードを掴んだときの配下の伝票 id。あれば選択より優先する */
    laneItemIds: readonly string[] | null,
  ): DraggedItem | null => {
    if (!unassigned.some((item) => item.id === activeId)) return null;
    const isMulti = selectedUnassignedIds.has(activeId) && selectedUnassignedIds.size > 1;
    // 地点単位は「地点ごとにまとめて扱う表示」なので、選択の有無に関わらず配下を全件運ぶ
    const itemIds = laneItemIds ?? (isMulti ? Array.from(selectedUnassignedIds) : [activeId]);
    const legRunIds = itemIds
      .map(legRunIdOfItemId)
      .filter((id): id is number => id != null && legRuns.some((lr) => lr.id === id));
    return legRunIds.length === 0 ? null : { kind: 'unassigned', legRunIds };
  }, [unassigned, selectedUnassignedIds, legRuns]);

  /**
   * 割り付けた legRun の地点名。未割当リストのカードから引く。
   * 文言に出るのは 1 件のときだけだが、引けたぶんだけ渡して判定は
   * `dropAssignNotification` に委ねる（呼び出し側で件数の分岐を二重に持たない）。
   */
  /**
   * 落とした案件の地点名。legRunId と対で返す。
   *
   * Why not 名前の配列: 名前を引けない案件があると位置がずれる。割り付かなかったぶんを
   * あとから外す（`allocatedDropped`）ので、どの名前がどの案件のものかが要る。
   */
  const unassignedNamesOf = useCallback((legRunIds: readonly number[]): Map<number, string> => {
    const byLegRunId = new Map<number, string>();
    for (const id of legRunIds) {
      const name = unassigned.find((item) => legRunIdOfItemId(item.id) === id)?.storeName;
      if (name) byLegRunId.set(id, name);
    }
    return byLegRunId;
  }, [unassigned]);

  /**
   * 掴んでいるものに追従させる中身（`DragOverlay`）。
   *
   * Why not 掴んだ `UnassignedItem` をそのまま持つ: ドラッグ中もサーバ再取得と楽観反映で
   * 一覧は入れ替わる。id で覚えて毎回引き直せば、消えた案件のカードが浮いたまま残らない。
   */
  const [dragOverlay, setDragOverlay] = useState<LongHaulDragOverlayContent | null>(null);
  const dragOverlayItem = useMemo(
    () => (dragOverlay?.itemId == null
      ? undefined
      : unassigned.find((item) => item.id === dragOverlay.itemId)),
    [dragOverlay, unassigned],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeId = String(event.active.id);
    const trip = readTripDragData(event.active);
    const kind: DraggedKind = trip ? 'trip' : 'unassigned';
    const started = dragStartAnalytics(kind);
    sendAnalytics(started.action, started.buttonName, started.label);
    setDragOverlay(resolveDragOverlayContent({
      activeId,
      trip,
      isUnassignedItem: unassigned.some((item) => item.id === activeId),
      selectedUnassignedIds,
    }));
  }, [unassigned, selectedUnassignedIds, sendAnalytics]);

  // ESC やドロップ先なしで終わったときも消す。残ると掴んでいないカードが画面に浮いたままになる
  const handleDragCancel = useCallback(() => setDragOverlay(null), []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setDragOverlay(null);
    if (!over) return;

    const work = readWorkDropData(over);
    const drop: DropLocation | null = over.id === UNASSIGNED_DROPPABLE_ID
      ? { kind: 'unassigned' }
      : (work && { kind: 'work', shiftTruckId: work.shiftTruckId, targetRowId: work.targetRowId });
    if (!drop) return;

    const trip = readTripDragData(active);
    const dragged = trip
      ? ({ kind: 'trip', trip } as DraggedItem)
      : resolveDraggedUnassigned(String(active.id), readLaneItemIds(active));
    if (!dragged) return;

    const command = buildBoardCommand({ dragged, drop });
    if (!command) return;
    // 名前は反映より先に引く。applyCommand は未割当リストから落とした案件を消すので、
    // あとから引くと文言の地点名だけが空になる。
    // Why not command.legRunIds: BoardCommand には legRunIds を持たない種類（reorder）があり、
    // 型を絞るぶんだけ分岐が増える。掴んだもの（dragged）から引けば未割当のときしか渡らない
    const names = dragged.kind === 'unassigned'
      ? unassignedNamesOf(dragged.legRunIds)
      : new Map<number, string>();
    runBoardCommand(command, names);
  }, [runBoardCommand, resolveDraggedUnassigned, unassignedNamesOf]);

  // ── セルの色分け ─────────────────────────────────────────
  // 会社ごとに API が持つ設定。未登録の会社にも既定値（色分けなし）が返る
  const { data: cellColorData } = useQueryDispatchCellColors(isLicenseReady);
  const cellColors = cellColorData ?? DEFAULT_CELL_COLORS;
  const { mutateAsync: saveCellColors } = useMutationDispatchCellColors();

  /**
   * PUT へ送る 4 キーだけを切り出す。`areas` は API が配るマスタで、混ぜて送り返すと
   * 「会社が書き換えられる値」と区別がつかなくなる。
   */
  const cellColorSettings = useMemo<DispatchCellColorSettings>(() => ({
    mode: cellColors.mode,
    basis: cellColors.basis,
    area_colors: cellColors.area_colors,
    prefecture_colors: cellColors.prefecture_colors,
  }), [cellColors]);

  const handleCellColorSettingsChange = useCallback((next: DispatchCellColorSettings) => {
    // 変わった項目だけ出す。色だけ直したときに「区分を変えた」と数えない
    if (next.mode !== cellColorSettings.mode) sendAnalytics('select', 'セル色分け 区分', next.mode);
    if (next.basis !== cellColorSettings.basis) sendAnalytics('select', 'セル色分け 基準', next.basis);
    void saveCellColors(next)
      .catch((error: unknown) => {
        enqueueSnackbar(apiErrorMessage(error, '色分けの設定を保存できませんでした'), { variant: 'error' });
      });
  }, [saveCellColors, enqueueSnackbar, cellColorSettings, sendAnalytics]);

  /**
   * 運行 1 本の色。設定が変わったときだけ作り直す（バーの数だけ呼ばれるので、
   * 毎描画で新しい関数を渡すと memo 済みのボードが毎回描き直しになる）。
   */
  const cellColorOf = useCallback(
    (trip: LongHaulTrip) => resolveCellColor(cellColors, trip),
    [cellColors],
  );

  // ── 休み設定 ────────────────────────────────────────────
  const [openCell, setOpenCell] = useState<{
    truckId: number; driverId: number | null; date: string; anchorEl: HTMLElement;
  } | null>(null);

  const shiftsEnabled = isLicenseReady && selectedCompanyId != null;
  const { data: truckShiftData } = useQueryTruckShifts(
    selectedCompanyId ?? 0, startOn, endOn, shiftsEnabled,
  );
  const { mutateAsync: restMutateAsync, isPending: isResting } = useMutationTruckShiftRest();

  const shiftTruck = useMemo(
    () => (openCell ? (truckShiftData ?? []).find((t) => t.id === openCell.truckId) : undefined),
    [openCell, truckShiftData],
  );

  /**
   * 休みを入れる対象の勤務。押したレーンのドライバーで絞ったうえで、いちばん早く始まるもの。
   *
   * Why 絞る: 同じトラック・同じ日に 2 人の勤務があるとき、絞らないと A の操作で
   * B の勤務（画面では別の行）まで巻き込む。
   * Why 1 本だけ渡してよいか: API はこの勤務と同じレーンの勤務すべてに休みを適用する
   * （時間指定で分割したあとの 2 本目も一緒に消える）。
   */
  const restShiftId = useMemo(() => {
    if (!openCell || !shiftTruck) return null;
    const dayCells = mapShiftsToCells(shiftTruck.shifts, [openCell.date])[openCell.date] ?? [];
    const laneCells = dayCells.filter((cell) => (cell.shift.driver?.id ?? null) === openCell.driverId);
    const earliest = laneCells.map((cell) => cell.shift)
      .sort((a, b) => a.startAt.localeCompare(b.startAt))[0];
    return earliest?.id ?? null;
  }, [openCell, shiftTruck]);

  const handleSelectDayCell = useCallback((lane: Lane, day: string, anchorEl: HTMLElement) => {
    sendAnalytics('click', '日セル 選択');
    setOpenCell({ truckId: lane.truckId, driverId: lane.driverId, date: day, anchorEl });
  }, [sendAnalytics]);

  const handleSubmitRest = useCallback((rest: TruckShiftRestRequest) => {
    if (restShiftId === null) return;
    void restMutateAsync({ id: restShiftId, rest })
      .then((result) => {
        setOpenCell(null);
        enqueueSnackbar(
          result.working_hours_shortened
            ? '休みにしました（残りの勤務に収まるよう労働時間を短くしました）'
            : '休みにしました',
          { variant: 'success' },
        );
      })
      // 失敗したらポップオーバーは開けたまま。理由を読んでから選び直せるようにする
      .catch((error: unknown) => { enqueueSnackbar(apiErrorMessage(error, '休みにできませんでした'), { variant: 'error' }); });
  }, [restShiftId, restMutateAsync, enqueueSnackbar]);

  // ── 詳細（運行パネル → 地点パネル） ──────────────────────────
  /**
   * 開いている運行パネルの対象。運行そのものではなく barId で覚え、描くたびに最新の
   * レーンから引き直す。D&D やサーバ再取得で中身が変わったあとも、掴んだ時点の
   * 古い運行を操作しないため（載っていた行が消えればパネルも閉じる）。
   *
   * Why not rowId: 1 回転は積地ごとに複数のバーへ分かれるので、rowId では
   * 「どのバーを押したか」を指せない（同じ回転の別の積地のバーが開く）。
   */
  const [selectedTripBarId, setSelectedTripBarId] = useState<string | null>(null);
  /**
   * 積 1・降 1 で運行パネルを飛ばして地点パネルを開いたときの運行。
   *
   * 運行パネルを通らないので、運行全体に効く操作（未割当に戻す・他レーンへ移す）の
   * 適用先がここにしか残らない。`selectedTripBarId` と同じく barId で覚える。
   */
  const [directTripBarId, setDirectTripBarId] = useState<string | null>(null);
  /** 押したバーの矩形。運行パネルを押した位置へ寄せる（地点パネル側は hook が持つ） */
  const [tripClickRect, setTripClickRect] = useState<DetailPanelClickRect | null>(null);

  const findTripByBarId = useCallback((barId: string | null): LongHaulTrip | null => {
    if (barId == null) return null;
    for (const lane of lanes) {
      const found = lane.trips.find((trip) => trip.barId === barId);
      if (found) return found;
    }
    return null;
  }, [lanes]);

  const selectedTrip = useMemo(
    () => findTripByBarId(selectedTripBarId),
    [findTripByBarId, selectedTripBarId],
  );
  const directTrip = useMemo(
    () => findTripByBarId(directTripBarId),
    [findTripByBarId, directTripBarId],
  );
  /** 運行全体に効く操作の適用先。運行パネル経由でも直行でも同じ運行を指す */
  const actionTrip = selectedTrip ?? directTrip;

  const moveTargets = useMemo(
    () => (actionTrip
      ? buildLongHaulMoveTargets(lanes, { shiftTruckId: actionTrip.shiftTruckId })
      : []),
    [lanes, actionTrip],
  );

  const handleUnassignTrip = useCallback(() => {
    if (!actionTrip) return;
    setSelectedTripBarId(null);
    setDirectTripBarId(null);
    runBoardCommand(buildBoardCommand({
      dragged: { kind: 'trip', trip: actionTrip },
      drop: { kind: 'unassigned' },
    }));
  }, [runBoardCommand, actionTrip]);

  const handleMoveTrip = useCallback((target: { shiftTruckId: number; targetRowId: string }) => {
    if (!actionTrip) return;
    setSelectedTripBarId(null);
    setDirectTripBarId(null);
    runBoardCommand(buildBoardCommand({
      dragged: { kind: 'trip', trip: actionTrip },
      drop: { kind: 'work', shiftTruckId: target.shiftTruckId, targetRowId: target.targetRowId },
    }));
  }, [runBoardCommand, actionTrip]);

  /**
   * 地点パネルのアクション層。カード配車・地図配車と同じ hook を使う。
   *
   * Why not 座標更新だけ自前で書く: この hook は mutation だけでなく「成功後にどのクエリを
   * 取り直すか」まで持っている（SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY を取り直すことで、
   * legRunMaterialsMap 経由でこの画面のカード座標も入れ替わる）。書き写すと 3 つ目の実装になる。
   * 併せて、選択カードを毎 render 最新の rowGroups から引き直す（更新後に古い座標を出さない）。
   */
  const applyResetToCaches = useApplyResetToCaches();
  const { operationBulkResetting } = useMutationPlanningV5();
  const { bulkDelete: deleteShipmentOrders, cloneShipmentOrder } = useMutationShipmentOrder();
  const refetchLegRuns = useCallback(() => { void legRunsQuery.refetch(); }, [legRunsQuery]);
  const notify = useCallback(
    (message: string, variant?: 'success' | 'error' | 'info') => { enqueueSnackbar(message, { variant }); },
    [enqueueSnackbar],
  );

  // ── 車両 / 車番 / ドライバーの差し替え（行の中のチップ） ──────────
  /**
   * サーバ反映を待たずに表示だけ先に切り替える。キーは勤務 (shift_truck)。
   * 上書きを被せる先は行グループなので、レーンのグループへ被せる。
   */
  const {
    overrides: vehicleOverrides, setVehicleOverride, clearVehicleOverride,
  } = useVehicleDisplayOverrides();
  const displayLaneRowGroups = useMemo(
    () => applyVehicleOverrides(laneRowGroups, vehicleOverrides),
    [laneRowGroups, vehicleOverrides],
  );
  const queryClient = useQueryClient();
  const refetchAfterVehicleEdit = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] }),
    ]);
  }, [queryClient]);
  /**
   * `/map` とカード型配車と同じ hook。渡す行グループだけが違う。
   *
   * Why not 期間中の全勤務 (`rowGroups`): hook は「他の勤務が使っている車両・ドライバー」を
   * 候補から外す。期間が 7 日あると、ほとんどの車両がどこかの日に勤務を持つので候補が
   * 空になる。画面に出ている単位（レーン）で判断させる。
   */
  const {
    handlers: vehicleAssignmentHandlers,
    menuProps: vehicleAssignmentMenuProps,
  } = useVehicleAssignmentEditing({
    rowGroups: displayLaneRowGroups,
    deliveries,
    trucks,
    drivers,
    refetchAfterEdit: refetchAfterVehicleEdit,
    notify,
    setVehicleOverride,
    clearVehicleOverride,
  });

  /** 会社・期間の同一性。楽観削除と詳細アクションが「操作を始めた時点の scope」の判定に使う */
  const scopeKey = buildPlanningScopeKey(companyId, startOn, endOn);

  /**
   * 未割当の楽観削除。「案件を削除」が読む。
   *
   * Why not 専用の機構を足す: 未割当の表示上書きは `useDispatchAssignment` の
   * `overrideUnassigned` が既に持っている。別の機構を足すと 1 画面に楽観状態が 2 つ並び、
   * どちらが表示を決めるのか決まらなくなる。カード配車と同じ hook を、同じ
   * `overrideUnassigned` へ繋ぐ（カード配車も `useDispatchAssignment` の override が相手）。
   */
  const unassignedOptimistic = useUnassignedRemovalOptimistic({
    serverItems: serverUnassigned,
    overrideItems: overrideUnassigned,
    setOverrideItems: setOverrideUnassigned,
    selectedIds: selectedUnassignedIds,
    setSelectedIds: setSelectedUnassignedIds,
    scopeKey,
  });
  /**
   * 確認ダイアログ。「案件を削除」「案件をコピー」が開く。
   * setState 関数は参照が安定しているのでそのまま渡す（カード配車 Presenter.tsx と同じ）。
   */
  const [confirmDialog, setConfirmDialog] = useState<{
    /** 省略すると本文だけの確認になる */
    title?: string;
    description: string;
    onConfirm: () => void | Promise<void>;
  } | null>(null);

  // hook は自分の state しか捨てられないので、画面ローカルの選択はここで捨てる
  const handleScopeChanged = useCallback(() => {
    setSelectedTripBarId(null);
    setDirectTripBarId(null);
    setSelectedLaneIds(new Set());
    setSelectedUnassignedIds(new Set());
    setOpenCell(null);
  }, []);

  const detailActions = useDispatchDetailActions({
    deliveries,
    // 表示中の盤面（楽観的上書き込み）。選択カードの引き直しもここを見る
    rowGroups,
    legRuns,
    // 座標編集で同一住所の地点をまとめて直す (#338 C14) 母集団。右カラムへ渡しているものと同じ配列
    unassignedItems: unassigned,
    screenName: SCREEN_NAMES.PLANNING_LONG_HAUL,
    scopeKey,
    applyResetToCaches,
    operationBulkResetting,
    deleteShipmentOrders,
    cloneShipmentOrder,
    refetchLegRuns,
    setConfirmDialog,
    notify,
    unassignedOptimistic,
    onScopeChanged: handleScopeChanged,
    // openTransferPlanning を渡さない = hook が handleMoveToVehicle に undefined を返し、
    // 器が「他車両へ配車」を描かない（既存 2 画面と同じ、注入で出し分ける契約）
  });
  const {
    detailSelection, detailClickRect, openDetail, closeDetail, moveMenuAnchor, setMoveMenuAnchor,
    duplicateStoreNames, geoAlerts,
  } = detailActions;

  // ── 一括操作（既存 2 画面と同じ入口） ──────────────────────
  /**
   * 選択したレーンの配送計画をまとめて未割当に戻す。
   * 対象の集め方は `/map`・カード配車と共有する（片方だけ直すと削除件数が食い違う）。
   */
  const handleUnassignSelected = useCallback(() => {
    if (selectedRowIds.size === 0) return;

    const { legRunIds: ids, deletableRowCount } = collectResetTargets(selectedRowIds, serverRowGroups);
    setConfirmDialog({
      title: '配送計画を削除しますか？',
      description: `${selectedLaneIds.size}件の配送計画を削除します。`,
      onConfirm: async () => {
        if (ids.length === 0) {
          // 案件の載っていないレーンだけを選んでいても結果は伝える
          notify(`${deletableRowCount}件の配送計画を削除しました`, 'success');
          return;
        }
        try {
          await operationBulkResetting.mutateAsync(ids);
        } catch (e) {
          notify(apiErrorMessage(e, '処理に失敗しました'), 'error');
          return;
        }
        applyResetToCaches(ids);
        notify(`${deletableRowCount}件の配送計画を削除しました`, 'success');
      },
    });
  }, [selectedRowIds, serverRowGroups, selectedLaneIds.size, operationBulkResetting,
    applyResetToCaches, notify]);

  /** 選択した未割当案件をまとめて削除する。楽観的に消してから投げるのも既存 2 画面と同じ */
  const handleDeleteSelectedUnassigned = useCallback(() => {
    const ids = Array.from(selectedUnassignedIds);
    if (ids.length === 0) return;
    const shipmentOrderIds = resolveShipmentOrderIdsFromUnassignedIds(ids, legRuns);
    if (shipmentOrderIds.length === 0) {
      notify('削除対象の案件が見つかりません', 'error');
      return;
    }
    // 1 伝票が複数の legRun を持つので、消える legRun を全部集めてから画面から外す
    const shipmentOrderIdSet = new Set(shipmentOrderIds);
    const removedItemIds = new Set<string>();
    legRuns.forEach((lr) => {
      if (lr.id != null && shipmentOrderIdSet.has(lr.shipment_order_id)) removedItemIds.add(`lr-${lr.id}`);
    });

    setConfirmDialog({
      title: '案件を削除しますか？',
      description: `${shipmentOrderIds.length} 件の案件を削除します。元には戻せません。`,
      onConfirm: async () => {
        const removal = unassignedOptimistic.remove(removedItemIds);
        try {
          await deleteShipmentOrders.mutateAsync(shipmentOrderIds);
          notify(`${shipmentOrderIds.length} 件の案件を削除しました`, 'success');
          removal.commit();
        } catch (e) {
          notify(apiErrorMessage(e, '削除に失敗しました'), 'error');
          removal.rollback();
        }
      },
    });
  }, [selectedUnassignedIds, legRuns, unassignedOptimistic, deleteShipmentOrders, notify]);

  /**
   * 「同一地点で割当済」タグから車両行を選ぶ。
   * トグルにしない（タグから選ぶのは「そこを見たい」意図しかないので、すでに選ばれて
   * いたときに解除すると意図と逆になる）。既存 2 画面と同じ。
   */
  const handleSelectVehicleRows = useCallback((rowIds: readonly string[]) => {
    const laneIds = rowIds.map((rowId) => laneIdByRowId.get(rowId)).filter((id): id is string => id != null);
    if (laneIds.length === 0) return;
    setSelectedLaneIds((prev) => {
      if (laneIds.every((laneId) => prev.has(laneId))) return prev;
      const next = new Set(prev);
      laneIds.forEach((laneId) => next.add(laneId));
      return next;
    });
  }, [laneIdByRowId]);

  const { restoreSplitLegRun } = useMutationShipmentOrderLegRun();
  const restoreSplitLegRunMutate = restoreSplitLegRun.mutate;

  /** 分割した案件を元に戻す。確認してから投げるのも文言も既存 2 画面と同じ */
  const handleRestoreSplit = useCallback((item: UnassignedItem) => {
    const legRunId = item.id.startsWith('lr-') ? parseInt(item.id.slice(3), 10) : NaN;
    if (Number.isNaN(legRunId)) return;
    setConfirmDialog({
      title: '分割した案件を復元しますか？',
      description: '分割した案件を元に戻します。よろしいですか？',
      onConfirm: () => {
        restoreSplitLegRunMutate(legRunId, {
          onSuccess: () => {
            notify('分割した案件を元に戻しました', 'success');
            refetchLegRuns();
          },
          onError: () => { notify('分割した案件を元に戻す処理でエラーが発生しました', 'error'); },
        });
      },
    });
  }, [restoreSplitLegRunMutate, notify, refetchLegRuns]);

  // ── 運賃計算 ────────────────────────────────────────────
  const [tariffDialogOpen, setTariffDialogOpen] = useState(false);
  const tariffAction = useTariffCalculationAction();
  const { targetIds: tariffTargetIds, disabledReason: tariffDisabledReason } = useMemo(
    () => resolveLongHaulTariffAvailability({
      deliveries,
      isLoading: deliveriesQuery.isLoading,
      isError: deliveriesQuery.isError,
      isBusy: controller.isBusy,
      isResetting: operationBulkResetting.isPending,
      isCalculatingTariff: tariffAction.isExecuting,
    }),
    [deliveries, deliveriesQuery.isLoading, deliveriesQuery.isError, controller.isBusy,
      operationBulkResetting.isPending, tariffAction.isExecuting],
  );

  // ── 進行中トースト ──────────────────────────────────────
  /**
   * 右下に出す実行中の知らせ。文言の決め方は既存 2 画面と共有する。
   *
   * 出す条件はカード配車に合わせて `controller.isBusy`（= POST を投げた時点から）にする。
   * `/map` の `isAccepted`（サーバが受け付けてから）だと、投げてから受け付けまでの間が
   * 無表示になる。この画面は D&D でも自動配車が走るので、その間に「効いていない」と
   * 思わせたくない（ユーザー指摘 2026-09-29）。
   *
   * 車両の差し替えはこの画面に無いので常に false。
   */
  const progressToastLabel = resolveProgressToastLabel({
    isResetting: operationBulkResetting.isPending,
    isAutoDispatching: controller.isBusy,
    isApplyingVehicleChange: false,
    isCalculatingTariff: tariffAction.isExecuting,
  });

  /**
   * 停止を出せる条件。走っている計算を捨てるので確認を挟む（既存 2 画面と同じ文言）。
   *
   * 未割当に戻す最中は出さない。API が 1 本で途中から止められないので、
   * 押しても何も起きないボタンになる。
   */
  const canStopAutoDispatch = controller.isBusy && !operationBulkResetting.isPending;
  const handleStopAutoDispatch = useCallback(() => {
    ReactGA.event('click', {
      screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
      button_name: '自動配車 停止',
    });
    setConfirmDialog({
      title: '自動配車を停止しますか？',
      description: '実行中の自動配車を停止します。よろしいですか？',
      // ユーザーが押した停止。止められなかったときは黙って捨てず伝える
      onConfirm: () => { controller.cancel({ notifyOnFailure: true }); },
    });
  }, [controller]);

  const handleOpenTariffCalculation = useCallback(() => {
    // D&D の確定は最後の操作から 2 秒後に飛ぶ。その待ち時間は isBusy にならないので、
    // 古い配車のまま計算して保存しないよう押した瞬間に見る（ref は再描画を起こさないので
    // disabled には反映できない）
    if (hasQueuedOverrideChangeRef.current) {
      notify(PENDING_ASSIGNMENT_TARIFF_REASON, 'error');
      return;
    }
    setTariffDialogOpen(true);
  }, [hasQueuedOverrideChangeRef, notify]);

  // 重複店舗名・座標の警告。/map の detailPanelProps と同じ導出
  const detailPanelProps = useMemo(() => {
    if (!detailSelection) return null;
    const deliveryCard = detailSelection.source === 'vehicle' && detailSelection.card.type === 'delivery'
      ? detailSelection.card
      : null;
    const selectedId = detailSelection.source === 'vehicle'
      ? detailSelection.card.id
      : detailSelection.item.id;
    return {
      hasDuplicate: deliveryCard ? duplicateStoreNames.has(deliveryCard.storeName) : false,
      geoAlert: geoAlerts.get(selectedId),
    };
  }, [detailSelection, duplicateStoreNames, geoAlerts]);

  /**
   * 地点のカードを既存の詳細パネルへ渡す。運行パネルの地点を押した経路と、積 1・降 1 で
   * 直行する経路が同じ組み立てを通る（`DispatchSelection` の作り方を二重に書かない）。
   */
  const openStopDetail = useCallback((
    trip: LongHaulTrip, card: DispatchCard, clickRect: DetailPanelClickRect | null,
  ) => {
    const group = rowGroups.find((g) => g.rows.some((row) => row.rowId === trip.rowId));
    const row = group?.rows.find((r) => r.rowId === trip.rowId);
    if (!group || !row) return;
    const orderIndex = row.cards.findIndex((c) => c.id === card.id);
    setSelectedTripBarId(null);
    openDetail(
      { source: 'vehicle', card, vehicle: group.vehicle, orderIndex: orderIndex < 0 ? 0 : orderIndex },
      clickRect,
    );
  }, [openDetail, rowGroups]);

  /**
   * バーを押したとき。降地が 1 箇所の運行は地点を選ばせず、その降地の詳細パネルへ直行する
   * （ユーザー判断 2026-09-24: 長距離は 1 台 1 日 1 案件が多く、毎回の地点選択が過剰）。
   * 降地が 2 箇所以上なら従来どおり運行パネルを出す。判定は `resolveDirectStopCard`。
   */
  const handleSelectTrip = useCallback((trip: LongHaulTrip, clickRect: DetailPanelClickRect) => {
    // 降地の数を label に出す。直行（1 箇所）と運行パネル（2 箇所以上）の比率が読める
    sendAnalytics('click', '運行バー 選択', trip.unloadingNames.length);
    closeDetail();
    const directCard = resolveDirectStopCard(trip);
    if (directCard) {
      setDirectTripBarId(trip.barId);
      openStopDetail(trip, directCard, clickRect);
      return;
    }
    setDirectTripBarId(null);
    setSelectedTripBarId(trip.barId);
    setTripClickRect(clickRect);
  }, [closeDetail, openStopDetail, sendAnalytics]);

  /** 運行パネルの地点を押したとき。運行パネルは閉じ、そのカードの詳細パネルへ入れ替える */
  const handleSelectStop = useCallback((card: DispatchCard) => {
    if (!selectedTrip) return;
    // clickRect は渡さない。押した地点は運行パネルの中なので、そこへ寄せると
    // 入れ替わる前のパネルと重なる（既定位置＝画面右に出す）
    openStopDetail(selectedTrip, card, null);
  }, [openStopDetail, selectedTrip]);

  /**
   * 右カラムの未割当カードの ⓘ。押したカードの詳細パネルを開く。
   *
   * clickRect は渡さない。未割当リストは画面の右端にあるので、押した位置へ寄せると
   * パネルが左へ折り返してリスト自身を丸ごと覆っていた（実測）。渡さなければ
   * `asideWidth` を使った固定位置＝リストのすぐ左に出る（ユーザー判断 2026-09-28）。
   *
   * 運行パネルとは排他にする。両方を同じ枠へ出しており、下の描画は運行パネルを
   * 優先するので、閉じないと ⓘ を押しても何も変わらない。
   */
  const handleShowUnassignedDetail = useCallback(
    (item: UnassignedItem) => {
      setSelectedTripBarId(null);
      setDirectTripBarId(null);
      openDetail({ source: 'unassigned', item }, null);
    },
    [openDetail],
  );

  const handleCloseDetail = useCallback(() => {
    setSelectedTripBarId(null);
    setDirectTripBarId(null);
    closeDetail();
  }, [closeDetail]);

  /**
   * 直行したときの運行全体の操作。器のヘッダー（`source === 'vehicle'` で出る
   * 「未割り当てに戻す」「他車両へ配車」）へ、運行パネルと同じハンドラを繋ぐ。
   *
   * Why not 運行パネルと同じ文言にする: 器は `/map`・カード型配車と同一実体で、
   * 文言を変えると既存 2 画面まで変わる。適用先は同じ運行なので、押した結果は
   * 「他レーンへ移す」と一致する。
   *
   * 候補は勤務単位のまま渡す（`rowId` に投入先の行 id を入れる）。器は選ばれた
   * `rowId` しか返さないので、勤務 id はこちらで引き直す。
   */
  const detailMoveTargets = useMemo<DetailPanelMoveTarget[]>(
    () => moveTargets.map(({ targetRowId, label, subLabel }) => ({ rowId: targetRowId, label, subLabel })),
    [moveTargets],
  );

  const handleResetTripFromStop = useCallback(() => {
    closeDetail();
    handleUnassignTrip();
  }, [closeDetail, handleUnassignTrip]);

  const handleMoveTripFromStop = useCallback((targetRowId: string) => {
    const target = moveTargets.find((it) => it.targetRowId === targetRowId);
    if (!target) return;
    closeDetail();
    handleMoveTrip(target);
  }, [closeDetail, handleMoveTrip, moveTargets]);

  // ── 右カラム ───────────────────────────────────────────
  // 表示項目・表示対象はツールバーの「表示項目」から操作する。保存先（localStorage）は
  // 表示日数の区分ごとに分ける (log_web_issues#338 C7)。1 日ぶんは /map・カード配車と
  // 同じキーなので、1 日で開いたときは既存 2 画面と同じ設定が効く
  const displayMetricScope = displayMetricScopeOf(spanDaysOf(startOn, endOn));
  const [displayMetrics, setDisplayMetrics, displayMetricSet] = useDisplayMetrics(displayMetricScope);
  const [orderAttrFilter, setOrderAttrFilter] = useOrderAttrFilter();
  /**
   * 未割当リストの表示単位 (log_web_issues#338 C8)。操作はツールバーの「表示項目」の中、
   * 効くのは右カラムの未割当リストと、別のコンポーネントにまたがるので Presenter が持つ。
   * 保存先は画面ごと（地図で地点単位にしてもこの画面は変わらない）。
   */
  const [unassignedDisplayMode, setUnassignedDisplayMode] = useUnassignedDisplayMode(
    SCREEN_NAMES.PLANNING_LONG_HAUL,
  );
  const handleSelectUnassigned = useCallback((id: string) => {
    setSelectedUnassignedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── 自動配車（ツールバー） ────────────────────────────────
  // controller は useDispatchAssignment が作る（D&D の自動保存と同じもの）。
  // 押したときに開くダイアログ・可否の 2 層は /map と同じ部品で組む。
  const [planningDialogOpen, setPlanningDialogOpen] = useState(false);
  const relaxedRules = useMemo(() => RelaxedRuleValues.map((it) => it), []);
  const [selectedRelaxedRules, setSelectedRelaxedRules] = useState<RelaxedRuleVo[]>([]);
  const [priorityLargeTrucks, setPriorityLargeTrucks] = useState<number>(0);
  const { settings: planningDialogSettings, presenterProps: planningDialogSettingProps } = usePlanningDialogSettings();

  /** 層 2: ライセンス・実行中。選択の状態はここに入れない（層を混ぜると直し方が伝わらない） */
  const autoDispatchAvailability = useMemo(
    () => resolveAutoDispatchAvailability({
      isLoading: deliveriesQuery.isLoading,
      useAlgorithmPlanning,
      selectedCompanyId,
      isBusy: controller.isBusy,
      isResetting: operationBulkResetting.isPending,
      kind: 'normal',
    }),
    [deliveriesQuery.isLoading, useAlgorithmPlanning, selectedCompanyId,
      controller.isBusy, operationBulkResetting.isPending],
  );

  /**
   * 層 3 相当の「選択」の層。判定は /map・カード配車と同じ関数を使う。
   *
   * 「計画あり」の見方はカード配車に合わせて `hasDispatchableCard` を渡す。この画面は
   * D&D で置いた運行を自動配車で保存する流れがあり、置いた直後のカードは status が
   * template（未反映）なので、サーバの配車だけを見ると「割り当てるものが無い」と
   * 誤判定して保存できなくなる。
   */
  const autoDispatchSelectionReason = useMemo(() => {
    const hasSelectedVehicle = rowGroups.some(
      (group) => group.rows.some((row) => selectedRowIds.has(row.rowId)),
    );
    return resolveAutoDispatchSelectionReason({
      hasSelectedVehicle,
      allSelectedVehiclesHaveNoPlan: allSelectedShiftsHaveNoPlan(
        selectedRowIds, rowGroups, hasDispatchableCard,
      ),
      hasSelectedUnassigned: selectedUnassignedIds.size > 0,
    });
  }, [rowGroups, selectedRowIds, selectedUnassignedIds]);

  const isAutoDispatchDisabled = autoDispatchAvailability.disabled || autoDispatchSelectionReason !== null;
  // ライセンス等の理由を優先する。選択を直しても押せないものを先に伝える
  const autoDispatchTooltip = autoDispatchAvailability.disabled
    ? autoDispatchAvailability.tooltip
    : autoDispatchSelectionReason ?? autoDispatchAvailability.tooltip;

  // ダイアログへ渡す対象。選択レーン → delivery.id（= shift_truck.id）→ delivery.shiftId。
  // 同じ shift.id を複数の勤務が指しうるので、写したあとに改めて重複排除する
  const selectedShiftIds = useMemo(() => {
    const deliveryIds = collectSelectedDeliveryIdsForPlanning(selectedRowIds, rowGroups);
    const shiftIdSet = new Set<number>();
    deliveryIds.forEach((deliveryId) => {
      const shiftId = deliveries.find((d) => d.id === deliveryId)?.shiftId;
      if (shiftId != null) shiftIdSet.add(shiftId);
    });
    return [...shiftIdSet];
  }, [selectedRowIds, rowGroups, deliveries]);

  const selectedOrderIds = useMemo(
    () => parseUnassignedIds(selectedUnassignedIds),
    [selectedUnassignedIds],
  );

  const handleAutoDispatchClick = useCallback(() => {
    if (selectedShiftIds.length === 0) {
      enqueueSnackbar('配車する車両を選択してください');
      return;
    }
    setPlanningDialogOpen(true);
  }, [selectedShiftIds, enqueueSnackbar]);

  const handlePlanningDialogClose = useCallback(() => setPlanningDialogOpen(false), []);

  /**
   * ダイアログの「実行する」。通知・完了後の後始末は controller の lifecycle
   * （useDispatchAssignment が組んだ 8 hooks）が持つので、ここは planning の組み立てだけ。
   *
   * Why not selectedCycleIndexes / deliveryOperations を送る: どちらも「何回転目を対象に
   * するか」「今の運行順」を指定する値で、回転の概念を画面に出していないこの画面には
   * 決める材料が無い。カード配車（ConvenienceDispatches/Presenter.tsx:1852-1853）と
   * 同じく空で送る。
   */
  const handlePlanningRequest = useCallback((shiftIds: number[], orderIds: number[]) => {
    if (shiftIds.length === 0) {
      enqueueSnackbar('配車する車両を選択してください');
      return;
    }
    void controller.runNormal({
      shiftIds,
      orderIds,
      planning: buildPlanningEntity(planningDialogSettings, {
        startOn, endOn, selectedRelaxedRules, priorityLargeTrucks,
      }),
      requestAsync: true,
      selectedCycleIndexes: [],
      deliveryOperations: [],
      planningSetting: {
        deliveryConditions: {},
        orderConditions: {},
        planningConditions: {},
      } as unknown as PlanningSettingRequest,
    });
  }, [
    controller, planningDialogSettings, startOn, endOn,
    selectedRelaxedRules, priorityLargeTrucks, enqueueSnackbar,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${appBarHeight}px)`,
        overflow: 'hidden',
      }}
    >
      {/* 内容なりに伸びる。折り返して 2 段になっても下段が押し出されるだけ（/map と同じ） */}
      <Box sx={{ flexShrink: 0 }}>
        <LongHaulToolbar
          onNavigate={onNavigate}
          startOn={startOn}
          endOn={endOn}
          onDateRangeChange={onDateRangeChange}
          onAutoDispatch={handleAutoDispatchClick}
          isAutoDispatchDisabled={isAutoDispatchDisabled}
          autoDispatchTooltip={autoDispatchTooltip}
          displayMetrics={displayMetrics}
          onDisplayMetricsChange={setDisplayMetrics}
          orderAttrFilter={orderAttrFilter}
          onOrderAttrFilterChange={setOrderAttrFilter}
          customInputFieldNames={customInputFieldNames}
          isLicenseReady={isLicenseReady}
          cellColorSettings={cellColorSettings}
          cellColorAreas={cellColors.areas}
          onCellColorSettingsChange={handleCellColorSettingsChange}
          unassignedDisplayMode={unassignedDisplayMode}
          onUnassignedDisplayModeChange={setUnassignedDisplayMode}
        />
      </Box>

      <DndContext
        id={LONG_HAUL_DND_CONTEXT_ID}
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        <Box
          ref={paneContainerRef}
          data-testid="long-haul-panes"
          sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}
        >
          {/* 左 + 中央: 縦も横もこの 1 つの器だけがスクロールする。左カラムは sticky left で
              残す（横へスクロールしても車番が見えるように。カード配車と同じ形）。
              中の行は minHeight: '100%' で器の高さまで伸ばす。伸ばさないと、中身が器より
              高いときに左カラムの背景と境界線が器の高さで切れる。

              地色を盤面と同じトークンで塗るのは、盤面が日列の合計幅なりに伸びて器の右に
              余白を残すため。塗らないと body の灰色がそこだけ透け、日付のある側との境目が
              盤面の一部のように見える（サンプルは body 自体がほぼ白なので余白も白い） */}
          <Box sx={{
            flex: 1, minWidth: 0, overflowY: 'auto', bgcolor: 'hsl(var(--background))',
          }}
          >
            <Box sx={{ display: 'flex', minHeight: '100%', minWidth: 'max-content' }}>
              <Box
                data-testid="long-haul-vehicle-column"
                sx={{
                  width: VEHICLE_COLUMN_WIDTH,
                  flexShrink: 0,
                  position: 'sticky',
                  left: 0,
                  zIndex: 4,
                  borderRight: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                {/* 既存画面と見た目を揃えるため、カード型配車・地図配車と同じリストを流用する。
                    日ごとに変わる値（積載量・粗利・勤務時間・回転）だけを隠す */}
                <ConvenienceDispatchVehicleList
                  screenName={SCREEN_NAMES.PLANNING_LONG_HAUL}
                  rowGroups={displayLaneRowGroups}
                  isLoading={deliveriesQuery.isLoading}
                  displayMetrics={displayMetricSet}
                  hiddenDailyMetrics={LONG_HAUL_HIDDEN_DAILY_METRICS}
                  // 1 行 116px。横のレーンと外形を揃える（`layout.ts`）
                  cardHeight={LONG_HAUL_VEHICLE_CARD_HEIGHT}
                  grossProfitByTruckId={EMPTY_GROSS_PROFIT}
                  peakLoadByRowId={EMPTY_PEAK_LOAD}
                  driverNameById={driverNameById}
                  selectedRowIds={listSelectedRowIds}
                  allSelected={allLanesSelected}
                  hasSelection={selectedLaneIds.size > 0}
                  onRowToggle={handleRowToggle}
                  onSelectAll={handleSelectAllLanes}
                  onDeselectAll={handleDeselectAllLanes}
                  onUnassignSelected={handleUnassignSelected}
                  onAddVehicle={openAddVehicleDialog}
                  onOpenPrint={reportPrinting.open}
                  truckDisplayStatus={filterMode}
                  onTruckDisplayStatusChange={setFilterMode}
                  // 回転・制約違反・車両グループでの絞り込みは渡さない（メニューにも出ない）。
                  // 回転は 1 日の中の概念で、制約違反は行ごとの積載量が要る
                  truckGroupEntitiesWithAll={NO_TRUCK_GROUPS}
                  selectedTruckGroupIndices={NO_TRUCK_GROUP_INDICES}
                  onSelectedTruckGroupIndicesChange={noop}
                  // 回転の追加・削除はボタンごと出さないので到達しない（必須 prop のため渡す）
                  onAddRotation={noop}
                  onDeleteRotation={noop}
                  // 並べ替えの単位はトラック。1 台に複数レーンがあるときは
                  // `truckOrderFromKeys` が重複を落とす
                  canSortTruckDisplayOrder
                  isMutatingTruckOrder={truckDisplayOrderMutation.isPending}
                  onTruckOrderChange={handleTruckOrderChange}
                  // 車両 / 車番 / ドライバーの差し替え。差し替え先はレーンを代表する勤務
                  onTruckEdit={vehicleAssignmentHandlers.onTruckEdit}
                  onDriverEdit={vehicleAssignmentHandlers.onDriverEdit}
                  onPlateNumberEdit={vehicleAssignmentHandlers.onPlateNumberEdit}
                  // 行の左端の色バーと色ピッカー
                  vehicleColorFor={vehicleColorFor}
                  onVehicleColorClick={handleVehicleColorClick}
                />
              </Box>

              {/* 中央カラム: スクロールは持たず、日カラムの合計幅なりに伸びる。
                  縮めると中身がはみ出してボード側に縦スクロールが生まれ、左カラムとずれる */}
              <Box
                data-testid="long-haul-board-column"
                sx={{ minWidth: 'max-content', flexShrink: 0 }}
              >
                <LongHaulBoard
                  lanes={lanes}
                  days={days}
                  rowHeight={LONG_HAUL_ROW_HEIGHT}
                  dayColWidth={dayColWidth}
                  onSelectTrip={handleSelectTrip}
                  onSelectDayCell={handleSelectDayCell}
                  cellColorOf={cellColorOf}
                />
              </Box>
            </Box>
          </Box>

          {/* 畳んでいる間は出さない。幅 0 のパネルを掴めてしまう（カード配車と同じ条件）。
              左カラムは可変にしないので、ハンドルはここ 1 本だけ */}
          {isUnassignedOpen && (
            <PanelResizeHandle
              side="right"
              width={fittedUnassignedWidth}
              defaultWidth={DEFAULT_UNASSIGNED_PANEL_WIDTH}
              label="右リストの幅を調整"
              screenName={SCREEN_NAMES.PLANNING_LONG_HAUL}
              onWidthChange={setUnassignedWidth}
              onResizingChange={setUnassignedResizing}
              containerWidth={paneContainerWidth}
              reservedWidth={reservedWidthFor(VEHICLE_COLUMN_WIDTH)}
            />
          )}

          {/* 右カラム: 左端に半分はみ出す円ボタンで開閉する
              （サンプル route-planner MatchingPresenter/index.tsx:2110-2143。
              既存 2 画面も同じ形を持つ） */}
          <Box sx={{
            position: 'relative', display: 'flex', height: '100%', minHeight: 0, flexShrink: 0,
          }}
          >
            <Box
              component="button"
              type="button"
              onClick={toggleUnassignedPanel}
              aria-label={isUnassignedOpen ? '右パネルを閉じる' : '右パネルを開く'}
              sx={{
                position: 'absolute',
                left: -12,
                top: 12,
                zIndex: 40,
                display: 'flex',
                height: 24,
                width: 24,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '9999px',
                border: '1px solid hsl(var(--border))',
                bgcolor: 'hsl(var(--background))',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'hsl(var(--accent))' },
              }}
            >
              <Box
                component="svg"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                sx={{
                  color: 'hsl(var(--muted-foreground))',
                  transition: 'transform 0.2s',
                  transform: isUnassignedOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                }}
              >
                <polyline points="9 18 15 12 9 6" />
              </Box>
            </Box>

            <Box
              data-testid="long-haul-unassigned-column"
              sx={{
                height: '100%',
                minHeight: 0,
                borderLeft: 1,
                borderColor: 'divider',
                // 開閉アニメーションはドラッグ中だけ切る。効いたままだと指の動きに
                // 0.2s 遅れて追いかけてくる
                transition: unassignedResizing ? 'none' : 'width 0.2s',
                overflow: 'hidden',
                // 既定は 380。幅はハンドルのドラッグで変えられる（#338 C3）
                width: isUnassignedOpen ? fittedUnassignedWidth : 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ConvenienceDispatchUnassignedList
                screenName={SCREEN_NAMES.PLANNING_LONG_HAUL}
                deliveryListGrouping={deliveryListGrouping}
                items={unassigned}
                rowGroups={rowGroups}
                driverNameById={driverNameById}
                deliveries={enrichedDeliveries}
                isLoading={legRunsQuery.isLoading}
                displayMetrics={displayMetricSet}
                selectedItemIds={selectedUnassignedIds}
                onSelectItem={handleSelectUnassigned}
                onDeleteSelected={handleDeleteSelectedUnassigned}
                // 「同一地点で割当済 (N)」タグから車両行を選ぶ。タグ自体はリストが自分で出す
                onSelectVehicleRows={handleSelectVehicleRows}
                onRestoreSplit={handleRestoreSplit}
                onOpenTariffCalculation={handleOpenTariffCalculation}
                tariffCalculationDisabledReason={tariffDisabledReason ?? undefined}
                // カードの ⓘ。未指定だとボタンごと描かれない（既存 2 画面と同じ契約）
                onShowDetail={handleShowUnassignedDetail}
                // 未割当理由バッジ。4 つで 1 組（理由だけ渡しても車両名が引けない）。
                // NotAllocReasonPresenter が要求する型は PlanningsDeliveryEntity[] だが、
                // 読むのは id / truckId / driverId だけなのでキャストで渡す（既存 2 画面と同じ）。
                // キャストは実体を作らないので、enrichedDeliveries の参照はそのまま保たれる
                notAllocReasonsByOrderId={notAllocReasonsByOrderId}
                notAllocDeliveries={enrichedDeliveries as unknown as PlanningsDeliveryEntity[]}
                notAllocTrucks={trucks}
                notAllocDrivers={drivers}
                customInputFieldNames={customInputFieldNames}
                trucksForFilter={trucksForFilter}
                driversForFilter={driversForFilter}
                companyId={companyId}
                isLicenseReady={isLicenseReady}
                isMastersReady={isMastersReady}
                isMastersError={isMastersError}
                // 表示対象（配送形態・区間種別）の絞り込み。操作はツールバーの「表示項目」の中
                orderAttrFilter={orderAttrFilter}
                // 長距離では出さない 3 つ。readOnly は渡さない（未割当カードの D&D ごと死ぬ）
                // 配車可否と配車サマリは出す（ユーザー判断 2026-09-18）。固定ルートは issue で不要
                showAlignToFixedRoute={false}
                // 期間ぶんの案件が 1 本の一覧に並ぶので、積日ごとに束ねて中央ボードの
                // 日付列と対応づける（/map・カード型配車は 1 日を開く画面なので渡さない）
                groupByLoadingDate
                // 伝票単位 / 地点単位の切替 (log_web_issues#338 C8、ユーザー判断 2026-09-25)。
                // トグルはツールバーの「表示項目」の中なので、ここへは値だけ渡す。
                // 積日グループは地点単位でも外側に残る（loadingDateLaneGroups）
                displayMode={unassignedDisplayMode}
              />
            </Box>
          </Box>
        </Box>

        {/* 掴んでいるものに追従する 1 枚。掴んでいない間は null を渡す
            （本物の DragOverlay も active が無い間は中身ごと描かない） */}
        <DragOverlay>
          {dragOverlay && (
            <LongHaulDragOverlayCard
              content={dragOverlay}
              item={dragOverlayItem}
              displayMetrics={displayMetricSet}
              customInputFieldNames={customInputFieldNames}
              screenName={SCREEN_NAMES.PLANNING_LONG_HAUL}
            />
          )}
        </DragOverlay>
      </DndContext>

      {/* 運行パネル（children モード）。地点を押すと下の従来モードへ入れ替わる */}
      {selectedTrip && (
        <DetailPanelShell
          placement="anchor"
          clickRect={tripClickRect}
          screenName={SCREEN_NAMES.PLANNING_LONG_HAUL}
          moveMenuAnchor={moveMenuAnchor}
          onChangeMoveMenuAnchor={setMoveMenuAnchor}
          onClose={handleCloseDetail}
        >
          <LongHaulTripDetail
            trip={selectedTrip}
            moveTargets={moveTargets}
            onSelectStop={handleSelectStop}
            onUnassign={handleUnassignTrip}
            onMoveTo={handleMoveTrip}
          />
        </DetailPanelShell>
      )}

      {/*
        地点パネル（従来モード）。バーの地点と右カラムの未割当カードの ⓘ が同じ器を使う。
        出す操作は「選んだ 1 件に効くもの」だけに絞る。

        Why not 他車両へ配車 / 未割り当てに戻す: 運行パネルを経由して開いたときは、
        どちらも運行全体に効く操作として LongHaulTripDetail が既に持っている。地点単位でも
        出すと、同じ画面に適用先の違う同名の操作が 2 つ並び、どちらが何に効くのか押す前に
        分からない。**ただし直行（積 1・降 1）のときは運行パネルを通らないので、この 2 つは
        ここに出さないと到達できなくなる**。`directTrip` があるときだけ渡す。
        （器がこの 2 つを描くのは `source === 'vehicle'` のときだけなので、未割当の ⓘ から
        開いたときは渡しても出ない）
        Why not 荷主別内訳からの移動 (onMoveShippers): 運行パネル経由と同じ理由で渡さない。
        器は `onMoveShippers` が無ければ内訳を読み取り専用のままにする。
        案件編集・伝票を分割・案件を削除・案件をコピーは渡す。器が描くのは
        `source === 'unassigned'` のときだけなので、地点パネルの見た目は変わらない。
      */}
      {!selectedTrip && detailSelection && detailPanelProps && (
        <DetailPanelShell
          selection={detailSelection}
          placement="anchor"
          clickRect={detailClickRect}
          asideWidth={fittedUnassignedWidth}
          screenName={detailActions.screenName}
          hasDuplicate={detailPanelProps.hasDuplicate}
          geoAlert={detailPanelProps.geoAlert}
          onGeoUpdate={detailActions.handleGeoUpdate}
          onEditLane={detailActions.handleEditLane}
          onEditOrder={detailActions.handleEditUnassignedOrder}
          resolveShipmentOrderId={detailActions.resolveShipmentOrderId}
          onSplit={detailActions.handleOpenSplitDialog}
          onDelete={detailActions.handleDeleteUnassignedFromDetail}
          onCopy={detailActions.handleCopyUnassignedFromDetail}
          /*
            直行したときだけ積地を添える（ユーザー指摘 2026-09-29）。バーは「積地 1 +
            降地 N」の運行そのものなので、降地だけ出すとどこで積んだかが読めない。
            運行パネルを経由したときは渡さない。あちらは積地・降地を一覧で出していて、
            押した人がどちらを見たいか既に選んでいる。
          */
          originCard={directTrip ? resolveTripLoadingCard(directTrip) : null}
          onResetToUnassigned={directTrip ? handleResetTripFromStop : undefined}
          onMoveToVehicle={directTrip ? handleMoveTripFromStop : undefined}
          moveTargets={directTrip ? detailMoveTargets : undefined}
          moveMenuAnchor={moveMenuAnchor}
          onChangeMoveMenuAnchor={setMoveMenuAnchor}
          onClose={handleCloseDetail}
        />
      )}

      {/* 車両追加・帳票出力ともカード配車・`/map` と同一実体。開閉は各々が自前で持つ。
          期間は表示中のものをそのまま渡す（既存 2 画面は単日なので startOn = endOn だが、
          この画面は複数日。追加した勤務が期間内に収まらないと盤面に出ない） */}
      <ConvenienceDispatchAddVehicleDialog
        open={addVehicleDialogOpen}
        onClose={closeAddVehicleDialog}
        startOn={startOn}
        endOn={endOn}
        existingTruckIds={existingTruckIds}
        existingDriverIds={existingDriverIds}
        trucks={trucks}
        drivers={drivers}
      />
      <ReportPrintingDialogs {...reportPrinting.dialogProps} />

      {/* 詳細パネルから開く周辺ダイアログ 3 つ（レーン管理・案件編集・伝票分割）。
          既存 2 画面と同一実体。`startOn` は表示期間の先頭を渡す（案件編集が読むのは
          車両・ドライバーのマスタ取得日だけ） */}
      <DispatchDetailDialogs
        laneSheetId={detailActions.laneSheetId}
        onCloseLaneSheet={detailActions.closeLaneSheet}
        editShipmentOrderId={detailActions.editShipmentOrderId}
        onCloseEditShipmentOrder={detailActions.closeEditShipmentOrder}
        startOn={startOn}
        splitDialogOpen={detailActions.splitDialogOpen}
        splitTargetLegRun={detailActions.splitTargetLegRun}
        onCloseSplitDialog={detailActions.closeSplitDialog}
        onSplitSuccess={detailActions.handleSplitSuccess}
      />

      {/* 自動配車・運賃計算・未割当に戻すの実行中を右下で知らせる（カード配車と同じ器） */}
      <DispatchProgressToast
        label={progressToastLabel}
        onStop={canStopAutoDispatch ? handleStopAutoDispatch : undefined}
      />

      {/* 「案件を削除」「案件をコピー」の確認。既存 2 画面と同じ共通ダイアログ */}
      <ConfirmDialog
        open={confirmDialog !== null}
        title={confirmDialog?.title}
        message={confirmDialog?.description ?? ''}
        handleCancel={() => setConfirmDialog(null)}
        handleOk={() => {
          const cb = confirmDialog?.onConfirm;
          setConfirmDialog(null);
          if (cb) void cb();
        }}
      />

      {/* 自動配車の設定ダイアログ。V3Plans / カード配車 / `/map` と同じ PlanningPresenter。
          Why not hiddenSelectedOrderCount を渡す: 未割当の絞り込みで隠れた件数を出す props
          だが、この画面は絞り込みの状態を Presenter が持たない（未割当リストの内部）。
          /map・カード配車も渡していない */}
      <Dialog
        open={planningDialogOpen}
        onClose={handlePlanningDialogClose}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { maxWidth: PLANNING_DIALOG_MAX_WIDTH } }}
      >
        <PlanningPresenter
          screenName={SCREEN_NAMES.PLANNING_LONG_HAUL}
          {...planningDialogSettingProps}
          planningRequest={handlePlanningRequest}
          onClose={handlePlanningDialogClose}
          selectedDeliveryIds={selectedShiftIds}
          selectedOrderIds={selectedOrderIds}
          startOn={startOn}
          relaxedRules={relaxedRules}
          selectedRelaxedRules={selectedRelaxedRules}
          setSelectedRelaxedRules={setSelectedRelaxedRules}
          setPriorityLargeTrucks={setPriorityLargeTrucks}
          priorityLargeTrucks={priorityLargeTrucks}
        />
      </Dialog>

      {/* 運賃計算。対象は表示期間（最大 10 日）の配車済みルート全件。
          `/map` は 1 日ぶんしか取らないので対象も 1 日ぶんになる、という違いがある */}
      <TariffCalculationDialog
        dialogIsOpen={tariffDialogOpen}
        setDialogIsOpen={setTariffDialogOpen}
        selectedDeliveryIds={tariffTargetIds}
        targetDescription={(
          <>
            対象期間の配車済みルート
            <strong>
              全
              {tariffTargetIds.length}
              件
            </strong>
            について、運賃を再計算して保存します。
          </>
        )}
        // 進行と結果は右下のトーストで知らせるので、完了を待たずに閉じる（`/map` と同じ）
        onExecute={(params) => { setTariffDialogOpen(false); tariffAction.execute(params); }}
        isExecuting={tariffAction.isExecuting}
      />

      <LongHaulRestPopover
        open={openCell !== null && restShiftId !== null}
        anchorEl={openCell?.anchorEl ?? null}
        truckName={shiftTruck?.licensePlateValue ?? ''}
        date={openCell?.date ?? ''}
        submitting={isResting}
        onClose={() => setOpenCell(null)}
        onSubmit={handleSubmitRest}
      />

      {/* 車両 / ドライバー / 車番の変更 UI。常時マウントで anchor の有無だけで開閉する */}
      <VehicleAssignmentMenus {...vehicleAssignmentMenuProps} />

      {colorAnchor && (() => {
        const truck = truckById.get(colorAnchor.truckId);
        return (
          <VehicleColorPickerPopover
            anchorEl={colorAnchor.el}
            truckId={colorAnchor.truckId}
            currentTruckColor={truck?.color ?? null}
            partnerCompanyId={truck?.partnerCompanyId ?? undefined}
            currentPartnerCompanyColor={truck?.partnerCompanyColor ?? null}
            truckName={truckNameByTruckId.get(colorAnchor.truckId) ?? ''}
            partnerCompanyName={truck?.partnerCompanyName ?? null}
            onClose={() => setColorAnchor(null)}
          />
        );
      })()}
    </Box>
  );
};

export default LongHaulPresenter;
